import React, { useState, useEffect, useRef } from 'react';
import useBalancedRows from '../utils/useBalancedRows'; // (30 Oct 2026, decision 160)
import {
  Calendar, CalendarDays, Plus, Share2, Clock, MapPin, X, Link2,
  Info, Apple, Trash2, MessageSquare, Settings, Pencil, Bell, RefreshCw, BookOpen
} from 'lucide-react';
import CalendarNamePicker from './CalendarNamePicker'; // (2 Nov 2026, decision 170)
import SettingsModal from './SettingsModal';
import UserGuideModal from './UserGuideModal';
import HostBoard from './HostBoard';
import { formatSignedInAs, formatTime, getTzAbbr, TIMEZONES, getEndTime, formatEventDateByPref } from '../utils/formatting';
import StatusToast from './StatusToast';
import { historyReadingOf, indexLabelOf, notesWithKeyword } from '../utils/calendarHistory'; // (9 Oct 2026, 8.19/8.20, decisions 64-67)
import HistoryPanel from './HistoryPanel'; // (9 Oct 2026, 8.19/8.20, decision 58)
import PollDatesDialog from './PollDatesDialog'; // (11 Oct 2026, Stage 3)

// (23 Oct 2026, 8.43, decision 112) A YYYY-MM-DD date in full, e.g. "Sunday 18 October",
// read as it is so no time zone can shift the day. Same as PollPage.jsx's fullDayDate.
const fullDayDate = (isoDate) => {
  const iso = String(isoDate || '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return '';
  return new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'UTC' });
};

// Extracted from App.js (App.js modularization, Sep 2026) — the default
// fallthrough render: the main GA/owner dashboard (calendar header, event
// list, add/edit event form, share-calendar section, and every dashboard
// modal: rename/delete calendar, delete event, WhatsApp notify prompt,
// tier-limit error, notifications panel, Settings, User Guide).
//
// This is a large, mechanical extraction — every line of JSX below is
// byte-identical to the original inline block (extracted via `sed`, not
// retyped), and every external reference is passed down as a same-named
// prop rather than renamed, specifically to eliminate transcription risk
// on a block this size. API_BASE_URL is passed under its original
// all-caps name for the same reason — the extracted JSX references it
// directly and renaming it would have meant editing inside the block.
// Further internal decomposition (separate Header/EventList/EventForm
// components) is a reasonable follow-up but was out of scope for this
// pass.
const DashboardStep = ({
  // state (read)
  statusMessage,
  currentCalendar,
  calendars,
  userEmail,
  preferredName,
  firstName,
  lastName,
  isSA,
  raRegions,
  authorityCalendars,
  canCreateCalendar,
  userTier,
  showWeekday = true, // (24 Oct 2026, 8.40a, decision 100)
  events,
  sortedEvents,
  showAddEvent,
  showEditEvent,
  editingEvent,
  newEvent,
  isAddingEvent,
  eventUseCustomTz,
  eventDiffEndTz,
  eventStartTz,
  eventEndTz,
  calendarTimezone,
  whatsappNotifyModal,
  tierError,
  showRenameCalendar,
  renameCalendarInput,
  showDeleteConfirm,
  showDeleteEventConfirm,
  eventToDelete,
  pendingNotifications,
  showNotificationsPanel,
  resolvingNotificationId,
  showSettings,
  settingsInitialTab,
  showUserGuide,
  userPreferredCurrency,
  step,
  // setters
  setNewEvent,
  setEventUseCustomTz,
  setEventDiffEndTz,
  setEventStartTz,
  setEventEndTz,
  setShowAddEvent,
  setRenameCalendarInput,
  setCalendarTimezone,
  setShowRenameCalendar,
  setShowDeleteConfirm,
  setWhatsappNotifyModal,
  setTierError,
  setShowDeleteEventConfirm,
  setEventToDelete,
  setShowNotificationsPanel,
  setShowSettings,
  setSettingsInitialTab,
  setShowUserGuide,
  setFirstName,
  setLastName,
  setPreferredName,
  // handlers
  handleCreateNewCalendar,
  handleSwitchCalendar,
  // (28 Oct 2026, decision 154) Calendars where this person is only a member, listed in the
  // drop-down after those they administer; choosing one opens its member page.
  memberCalendars = [],
  onChooseCalendar,
  handleRenameCalendar,
  handleSaveCalendarTimezone,
  handleCalendarOptionsSaved, // (9 Oct 2026, decision 60)
  handleStartEventFromBoard,
  handleAnnounceHostBoard,
  hostBoardVersion,
  pendingHostSlotId,
  // (20 Sep 2026) Host board reconfirmation tasks, shown in the Notifications bell
  hostBoardTasks = [],
  resolvingBoardTaskId,
  handleSendBoardTask,
  handleEmailBoardTask,
  handleResolveBoardTask,
  handleHostBoardChanged,
  handleDeleteCalendar,
  addEvent,
  saveEditEvent,
  cancelEdit,
  startEditEvent,
  confirmDeleteEvent,
  deleteEvent,
  shareToWhatsApp,
  handleSendWaNotification,
  handleSendPollNotification,
  resolveNotification,
  isAuthenticated,
  clearAuthToken,
  showStatus,
  // misc
  editEventRef,
  API_BASE_URL,
  // (9 Sep 2026) Proxy-entry Pass 4 — passed through to SettingsModal ->
  // MembersTab, which calls it once it's resolved which member and which
  // open poll to answer on their behalf.
  onAnswerPollForMember,
  onRefreshEvents,
  onRefreshNotifications,
  onViewPollResults,
  onAnswerPollAsSelf,
  // (6 Oct 2026, H18 Stage 2) The hosting-schedule question raised by Add Event (or by
  // "Add it to the hosting schedule only for now"), and the answer / cancel handlers.
  scheduleChoice,
  onScheduleChoice,
  onCancelScheduleChoice,
  // (11 Oct 2026, Stage 3) Questions raised by Edit Event when the date changes:
  // { type: 'history', name, oldDate, newDate } or { type: 'poll', timetable }.
  editQuestion,
  onEditQuestionAnswer,
  onCancelEditQuestion,
  // (24 Oct 2026, decision 131) The Poll dates window for an event that is not moving:
  // { eventUid, title, mode: 'stay' | 'resume', timetable }, and its open / answer / cancel.
  pollDatesQuestion,
  onOpenPollDates,
  onPollDatesAnswer,
  onCancelPollDates,
}) => {
  // (30 Oct 2026, decision 160) Phones: one row of top buttons when they fit, else the fewest even rows.
  const topButtonsRef = useRef(null);
  useBalancedRows(topButtonsRef);
  const [isRunningScheduler, setIsRunningScheduler] = useState(false);
  // (11 Oct 2026, 8.37) Run Scheduler first says what it does, on screen (the
  // explanation used to be in its hover text only).
  const [confirmRunScheduler, setConfirmRunScheduler] = useState(false);
  const [pollActionInFlight, setPollActionInFlight] = useState(null); // eventId being acted on
  const [pollActionConfirm, setPollActionConfirm] = useState(null); // { type: 'open'|'close', event, poll? }
  // (26 Oct 2026, decision 145) "Withdraw / Restore event administrator rights": the
  // confirmation's event and direction ({ event, withdraw }), and whether it is saving.
  const [eaConfirm, setEaConfirm] = useState(null);
  const [eaSaving, setEaSaving] = useState(false);
  // (26 Sep 2026, H3) "Email N members?" — how many members the action would
  // email, after each member's own email setting. undefined = still counting,
  // null = couldn't tell, number = the count.
  const [pollEmailCount, setPollEmailCount] = useState(undefined);
  // (30 Sep 2026, H17) Members to choose as an event's host (names only,
  // plus whether they have saved a hosting location). Loaded when the event
  // form opens.
  const [hostCandidates, setHostCandidates] = useState([]);
  useEffect(() => {
    if (!(showAddEvent || showEditEvent) || !currentCalendar?.id) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/calendars/${currentCalendar.id}/hosting-candidates`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` },
        });
        const data = await res.json();
        if (!cancelled && res.ok && Array.isArray(data)) setHostCandidates(data);
      } catch (err) {
        if (!cancelled) setHostCandidates([]);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAddEvent, showEditEvent, currentCalendar?.id, API_BASE_URL]);

  // (3 Oct 2026, F1) The administrator's OWN subscription link (each person
  // now has their own); the calendar's shared link is retired.
  const [myFeedLink, setMyFeedLink] = useState(null); // { feedUrl, webcalUrl }
  useEffect(() => {
    setMyFeedLink(null);
    if (!currentCalendar?.id) return undefined;
    let cancelled = false;
    fetch(`${API_BASE_URL}/api/calendars/${currentCalendar.id}/me/feed-link`, {
      headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (!cancelled && d && d.feedUrl) setMyFeedLink(d); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [currentCalendar?.id, API_BASE_URL]);

  // (3 Oct 2026, F1) Tells the group how to subscribe: through Calendar Hub,
  // where each member finds their own link (no link to the feed itself).
  const shareHowToSubscribe = () => {
    const appUrl = 'https://calendar.telematrixconsulting.com';
    const calName = currentCalendar?.name || 'our calendar';
    const text = `To add ${calName} to your phone's calendar: open Calendar Hub at ${appUrl}, sign in, and tap "Subscribe to add this calendar to your phone, tablet and/or computer". `
      + `You only need to do this once. Each member has their own link, so please don't share yours.`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
  };
  // (5 Oct 2026, H18 Stage 1) One host per occasion.
  //  - Calendar without a hosting schedule: every event has a host, chosen here, and
  //    that choice counts as confirmed (map and Directions straight away).
  //  - Calendar with a hosting schedule: "Host wanted" is allowed. On an event that
  //    belongs to a schedule month, this is the month's host too; a new host there
  //    shows the map once they have confirmed on the schedule.
  const usesSchedule = currentCalendar?.host_board_enabled === true;

  // (9 Oct 2026, 8.19/8.20, decisions 50, 64-67) The calendar history's index value
  // (Hot Club: the Country). Shown under Notes when the calendar keeps a history with an
  // index column and the event is in the history (with a keyword: Notes beginning with
  // it, e.g. "Culinary provenance: Sumatra"). Pre-filled with the value used last time
  // for the same keyword value (from the History page's data); a new keyword value starts
  // empty, and changing it asks again. Saving with it empty says what is missing
  // (App.js addEvent/saveEditEvent).
  const keepsHistory = currentCalendar?.history_enabled === true;
  const historyKeyword = keepsHistory ? (currentCalendar?.history_keyword || '').trim() : '';
  const indexLabel = indexLabelOf(currentCalendar);
  // (9 Oct 2026, decision 58) History replaces the dashboard's content, under its banner.
  const [showHistory, setShowHistory] = useState(false);
  useEffect(() => { setShowHistory(false); }, [currentCalendar?.id]);
  const formOpen = showAddEvent || showEditEvent;
  // (decision 66) Add Event's Notes start with the keyword and one space (after any
  // schedule month's own note has been put in, so the keyword goes first).
  useEffect(() => {
    if (!showAddEvent || !historyKeyword) return;
    setNewEvent((prev) => ({ ...prev, description: notesWithKeyword(currentCalendar, prev.description) }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAddEvent]);
  const [indexByKeywordValue, setIndexByKeywordValue] = useState({});
  useEffect(() => {
    if (!indexLabel || !historyKeyword || !formOpen || !currentCalendar?.id) return undefined;
    let cancelled = false;
    fetch(`${API_BASE_URL}/api/calendars/${currentCalendar.id}/calendar-history`, {
      headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (cancelled || !d || !Array.isArray(d.meetings)) return;
        const map = {};
        // Oldest first, so the latest index value for each keyword value wins.
        [...d.meetings].sort((x, y) => x.date.localeCompare(y.date)).forEach((m) => {
          if (m.keywordValue && m.indexValue) map[m.keywordValue.trim().toLowerCase()] = m.indexValue;
        });
        setIndexByKeywordValue(map);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [indexLabel, historyKeyword, formOpen, currentCalendar?.id, API_BASE_URL]);
  const formReading = historyReadingOf(currentCalendar, newEvent.description);
  const formKeywordValue = formReading.keywordValue;
  const lastKeywordValueRef = useRef(null);
  // When a form opens, remember its keyword value as it stands (an edited event keeps its index value).
  useEffect(() => {
    lastKeywordValueRef.current = historyReadingOf(currentCalendar, newEvent.description).keywordValue;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAddEvent, showEditEvent, editingEvent?.id]);
  useEffect(() => {
    if (!indexLabel || !historyKeyword) return;
    const before = lastKeywordValueRef.current;
    if ((before || '').toLowerCase() === (formKeywordValue || '').toLowerCase()) return;
    lastKeywordValueRef.current = formKeywordValue;
    const known = formKeywordValue ? indexByKeywordValue[formKeywordValue.toLowerCase()] : null;
    setNewEvent((prev) => ({ ...prev, historyIndexValue: known || '' }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formKeywordValue]);
  // The list may arrive after the keyword value was typed: fill an empty box then.
  useEffect(() => {
    if (!formKeywordValue || (newEvent.historyIndexValue || '').trim()) return;
    const known = indexByKeywordValue[formKeywordValue.toLowerCase()];
    if (known) setNewEvent((prev) => ({ ...prev, historyIndexValue: known }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [indexByKeywordValue]);
  // (10 Oct 2026, decision 75) The caption under the index box. With a keyword value:
  //  - the box matches it (Hungary / Hungary: the provenance is its own country): no caption;
  //  - the box differs (Sumatra / Indonesia): "The country under which Sumatra will be
  //    shown in the calendar history.";
  //  - the box is empty (a new keyword value): "Give the country under which …".
  // Compared ignoring capitals and spaces at either end. "country" is the calendar's
  // index column name, in lower case.
  const indexCaption = () => {
    const label = indexLabel.toLowerCase();
    if (!formKeywordValue) return `Give this event's ${indexLabel}; the History page lists meetings by ${indexLabel}.`;
    const value = (newEvent.historyIndexValue || '').trim();
    if (!value) return `Give the ${label} under which ${formKeywordValue} will be shown in the calendar history.`;
    if (value.toLowerCase() === formKeywordValue.trim().toLowerCase()) return null;
    return `The ${label} under which ${formKeywordValue} will be shown in the calendar history.`;
  };
  const renderIndexBox = () => {
    if (!(indexLabel && formReading.inHistory)) return null;
    const caption = indexCaption();
    return (
      <div className="relative">
        <span className="absolute left-3 top-0 -translate-y-1/2 bg-white px-1 text-xs text-gray-500">{indexLabel}</span>
        <input
          type="text"
          value={newEvent.historyIndexValue || ''}
          onChange={(e) => setNewEvent({ ...newEvent, historyIndexValue: e.target.value })}
          className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none"
        />
        {caption && <p className="text-xs text-gray-500 mt-1">{caption}</p>}
      </div>
    );
  };

  // (5 Nov 2026, decision 173) The event date in the person's Date Format, with the day of
  // the week unless they untick it (Settings › Preferences), as on the event cards. The
  // browser's own date box drew the date in the browser's or the computer's format instead
  // (5 Nov: "05-Nov-2026" in Edge, Windows' Short date; "4 Nov 2026" on the iPhone). The
  // browser's date box is still there, invisible, over the whole box: tapping anywhere opens
  // its calendar picker (showPicker where the browser has it), and it keeps the date as
  // YYYY-MM-DD, so nothing else changes.
  const renderDateBox = () => (
    <div className="relative">
      <div className="relative flex items-center justify-between px-4 py-3 border-2 border-gray-200 rounded-lg bg-white focus-within:border-blue-500">
        <span className={newEvent.date ? 'text-gray-800' : 'text-gray-400'}>
          {newEvent.date
            ? formatEventDateByPref(newEvent.date, userTier?.preferred_date_format, showWeekday)
            : 'Choose the date'}
        </span>
        <CalendarDays className="w-5 h-5 text-blue-600 flex-shrink-0 ml-2" aria-hidden="true" />
        <input
          type="date"
          aria-label="Date"
          value={newEvent.date}
          onChange={(e) => setNewEvent({ ...newEvent, date: e.target.value })}
          onClick={(e) => {
            try { if (typeof e.currentTarget.showPicker === 'function') e.currentTarget.showPicker(); } catch (err) { /* the browser opens its own way */ }
          }}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
        />
      </div>
      {/* After the box, so it sits on its border without a layer of its own (a z-index would
          put it over the banner when the form scrolls under it). */}
      <span className="absolute left-3 top-0 -translate-y-1/2 bg-white px-1 text-xs text-gray-500 pointer-events-none">Date</span>
    </div>
  );

  const renderHostPicker = () => (
    <div className="relative">
      <span className="absolute left-3 top-0 -translate-y-1/2 bg-white px-1 text-xs text-gray-500">Host</span>
      <select
        value={newEvent.hostMemberId || ''}
        onChange={(e) => setNewEvent({ ...newEvent, hostMemberId: e.target.value })}
        className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none bg-white"
      >
        <option value="">{usesSchedule ? 'Host wanted (no host yet)' : 'Choose the host'}</option>
        {hostCandidates.map((c) => (
          <option key={c.memberId} value={c.memberId}>
            {c.name}{c.hasLocation ? '' : ' — no location saved yet'}
          </option>
        ))}
      </select>
      <p className="text-xs text-gray-500 mt-1">
        {usesSchedule
          ? 'For a hosting-schedule month this is the schedule\'s host too. The map in members\' calendars and the Directions link appear once the host has confirmed and has saved a hosting location.'
          : 'If the host has saved a hosting location, members get a map in their calendars and a Directions link in the app.'}
      </p>
    </div>
  );
  const locationHint = (
    <p className="text-xs text-gray-500 mt-1">
      Leave blank to show the host's name{usesSchedule ? ' (or "Host wanted")' : ''}; it then follows any change of host.
    </p>
  );
  // (30 Sep 2026, H15) After an event is added, edited or deleted: how many
  // members receive event emails (null = none to show).
  // (7 Oct 2026, decision 19) The prompt now has separate buttons: "Email the N members
  // who receive event emails", "Send via WhatsApp" and "Done". Each button does only its
  // own job and the prompt stays open, showing what has been done, until Done. (Skip used
  // to send the emails too whenever the box was ticked, which was misleading.)
  const EVENT_EMAIL_ACTION = { event_add: 'added', event_edit: 'changed', event_delete: 'cancelled' };
  const [eventEmail, setEventEmail] = useState({ count: null, status: 'idle', sent: 0 });
  const [waOpened, setWaOpened] = useState(false);
  useEffect(() => {
    const action = whatsappNotifyModal && EVENT_EMAIL_ACTION[whatsappNotifyModal.type];
    setEventEmail({ count: null, status: 'idle', sent: 0 });
    setWaOpened(false);
    if (!action || !currentCalendar?.id) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/calendars/${currentCalendar.id}/event-update-emails/count`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` },
        });
        const data = await res.json();
        if (!cancelled && res.ok && typeof data.count === 'number' && data.count > 0) {
          setEventEmail({ count: data.count, status: 'idle', sent: 0 });
        }
      } catch (err) {
        // No email button in the prompt; nothing else changes.
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [whatsappNotifyModal, currentCalendar?.id, API_BASE_URL]);
  const sendEventEmails = async (modal) => {
    const action = modal && EVENT_EMAIL_ACTION[modal.type];
    if (!action || !eventEmail.count || !currentCalendar?.id || eventEmail.status !== 'idle') return;
    setEventEmail((st) => ({ ...st, status: 'sending' }));
    const body = action === 'cancelled'
      ? { action, event: modal.deletedEvent || {} }
      : {
        action,
        eventUid: modal.eventId,
        reconfirm: !!modal.reconfirm, // (11 Oct 2026, decision 91)
        previousDate: modal.previousDate || undefined, // (21 Oct 2026, 8.42, decision 107)
      };
    try {
      const res = await fetch(`${API_BASE_URL}/api/calendars/${currentCalendar.id}/event-update-emails`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setEventEmail((st) => ({ ...st, status: 'sent', sent: data.sent || 0 }));
      } else {
        setEventEmail((st) => ({ ...st, status: 'idle' }));
        showStatus(data.error || 'Could not send the event emails', 'error');
      }
    } catch (err) {
      setEventEmail((st) => ({ ...st, status: 'idle' }));
      showStatus('Could not send the event emails', 'error');
    }
  };
  // (7 Oct 2026, decision 24, revised) The heading says what the panel is for and names
  // the event: 'Inform members of the deletion of "Test fix 4"?'. When two prompts come
  // in a row (a replacement: the deletion, then the new event), a line above says
  // "Message 1 of 2" / "Message 2 of 2".
  const EVENT_NOUN = { event_add: 'the addition of', event_edit: 'the change to', event_delete: 'the deletion of' };
  const notifySequence = (modal) => (modal && modal.sequence && modal.sequence.total > 1
    ? `Message ${modal.sequence.index} of ${modal.sequence.total}`
    : null);
  const notifyHeading = (modal) => {
    if (!modal) return 'Inform members?';
    const noun = EVENT_NOUN[modal.type];
    if (noun) {
      const name = modal.subject && String(modal.subject).trim() ? `"${String(modal.subject).trim()}"` : 'this event';
      return `Inform members of ${noun} ${name}?`;
    }
    if (modal.type === 'calendar_rename') return 'Inform members of the renaming of the calendar?';
    if (modal.type === 'host_board_update') return 'Inform members of the changes to the hosting schedule?';
    return 'Inform members?';
  };
  useEffect(() => {
    if (!pollActionConfirm) { setPollEmailCount(undefined); return; }
    const { type, event, poll } = pollActionConfirm;
    const reason = type === 'open' ? 'phase1_open' : (poll && poll.phase === 'offer' ? 'phase2_open' : 'phase2_closed');
    let cancelled = false;
    setPollEmailCount(undefined);
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/events/${event.eventUid}/polls/member-email-count?reason=${reason}`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` },
        });
        const data = await res.json();
        if (!cancelled) setPollEmailCount(res.ok && typeof data.count === 'number' ? data.count : null);
      } catch (err) {
        if (!cancelled) setPollEmailCount(null);
      }
    })();
    return () => { cancelled = true; };
  }, [pollActionConfirm, API_BASE_URL]);
  // (23 Sep 2026) Events linked to a row of the hosting schedule (null = board off or
  // not loaded). (6 Oct 2026, H18 Stage 2) Now { uids, oneOffUids, labels }: one-off
  // events get a badge, and the delete question names the event's row ("May 2027 #2").
  // The "Not on hosting schedule" badge has gone: every event now has a row.
  const [boardEventInfo, setBoardEventInfo] = useState(null);
  // (5 Oct 2026, H18 Stage 1) Deleting an event that belongs to a hosting-schedule month:
  // is the meeting off ('off' -> "No meeting") or to be re-dated ('redate' -> "Day to be
  // confirmed")? No default: the administrator chooses; Delete asks if nothing is chosen.
  const [deleteMonthChoice, setDeleteMonthChoice] = useState(null);
  const [deleteMonthNote, setDeleteMonthNote] = useState('Cancelled');
  const [deleteMonthAsk, setDeleteMonthAsk] = useState(false);
  // (9 Oct 2026, 8.19/8.20, decision 62) Deleting a past meeting that is in the calendar
  // history: keep it in the history (the default) or delete it from there too.
  const [deleteHistoryChoice, setDeleteHistoryChoice] = useState('keep');
  useEffect(() => {
    setDeleteMonthChoice(null);
    setDeleteMonthNote('Cancelled');
    setDeleteMonthAsk(false);
    setDeleteHistoryChoice('keep');
  }, [eventToDelete]);
  const [confirmEmailTaskId, setConfirmEmailTaskId] = useState(null); // host-board task whose "Email instead" is awaiting a second click

  // (10 Sep 2026) Shared date-reformatting helper — same logic already used
  // inline for tierError.message's M/D/YYYY dates, extracted so it can also
  // reformat the ISO (YYYY-MM-DD) dates in Poll Notifications (title line
  // and message_text) to the user's Preferences > Date Format setting.
  const formatIsoDateToPref = (isoDateStr, prefFormat) => {
    const [yyyy, mm, dd] = isoDateStr.split('-').map(Number);
    const names = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    const fmt = prefFormat || 'DD-MMM-YYYY';
    if (fmt === 'MM/DD/YYYY') return `${mm}/${dd}/${yyyy}`;
    if (fmt === 'DD/MM/YYYY') return `${dd}/${mm}/${yyyy}`;
    if (fmt === 'YYYY-MM-DD') return isoDateStr;
    return `${dd}-${names[mm-1]}-${yyyy}`;
  };

  // (10 Sep 2026) Manual poll control for last-minute/compressed-timeframe
  // events — no trigger-days value can mean "now" (both are days-before-
  // event offsets), so these call the two existing-but-previously-
  // frontend-less endpoints directly instead. Both require confirmation
  // via pollActionConfirm below rather than firing on click.
  // (26 Sep 2026, H3) notify: true only when the GA chose "Email N members";
  // the server sends nothing otherwise.
  // (26 Oct 2026, decision 145) Withdraw or restore an event's event administrator rights:
  // immediately, for that event only; the host stays the host, and no message is sent to
  // them. Then the event list is reloaded so the card shows the new state.
  const executeEventAdminChange = async () => {
    if (!eaConfirm || !currentCalendar?.id) return;
    const { event, withdraw } = eaConfirm;
    setEaSaving(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/calendars/${currentCalendar.id}/events/${event.eventUid}/event-admin`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ withdrawn: withdraw }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        showStatus(data.note || (withdraw ? 'Event administrator rights withdrawn.' : 'Event administrator rights restored.'), 'success', 6000);
        setEaConfirm(null);
        if (onRefreshEvents) await onRefreshEvents();
      } else {
        showStatus(data.error || 'Could not change the event administrator rights', 'error');
      }
    } catch (err) {
      showStatus('Could not change the event administrator rights', 'error');
    } finally {
      setEaSaving(false);
    }
  };

  const executePollAction = async (notify) => {
    if (!pollActionConfirm) return;
    const { type, event, poll } = pollActionConfirm;
    setPollActionInFlight(event.id);
    try {
      const url = type === 'open'
        ? `${API_BASE_URL}/api/events/${event.eventUid}/polls/open`
        : `${API_BASE_URL}/api/polls/${poll.id}/close`;
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ notify: notify === true }),
      });
      const data = await res.json();
      if (res.ok) {
        // (13 Sep 2026) The toast used to just say "Poll closed." regardless
        // of outcome — a GA closing Phase 1 with zero offers had no way to
        // know that from the toast alone, only by clicking into View
        // Results afterward. Now phase/outcome-aware, using the same
        // data.confirmPoll field the WhatsApp-modal branch below reads.
        let statusMsg = 'Poll closed.';
        if (type === 'open') {
          statusMsg = 'Phase 1 poll opened.';
        } else if (poll.phase === 'offer') {
          statusMsg = data.confirmPoll
            ? 'Phase 1 closed — Phase 2 (confirm) is now open.'
            : 'Phase 1 closed with no course offers — Phase 2 was not started. You may want to check in with the group directly.';
        } else if (poll.phase === 'confirm') {
          statusMsg = 'Phase 2 closed — poll cycle complete.';
        }
        // (26 Sep 2026, H3) Say whether members were emailed. A zero-offers
        // Phase 1 close never emails members, whatever was chosen.
        const emailsWent = notify === true && !(type === 'close' && poll.phase === 'offer' && !data.confirmPoll);
        statusMsg += emailsWent ? ' Members are being emailed.' : ' No member emails sent.';
        // (26 Sep 2026) 6 s rather than the 2 s default: this message now says
        // whether members were emailed, and is too long to read in 2 s.
        showStatus(statusMsg, (type === 'close' && poll.phase === 'offer' && !data.confirmPoll) ? 'info' : 'success', 6000);
        if (onRefreshEvents) await onRefreshEvents();
        // (27 Sep 2026, A6) The server now queues a bell notice for manual
        // opens/closes too; show it straight away.
        if (onRefreshNotifications) onRefreshNotifications();
        // (13 Sep 2026) Offer to prepare a WhatsApp message for the group
        // right after a manual Phase 1 open — every genuine open reaching
        // here is a real phase-1-open, so no ambiguity to check for.
        if (type === 'open') {
          setWhatsappNotifyModal({
            type: 'poll_phase1_open',
            calendarName: currentCalendar?.name,
            eventTitle: event.title,
            eventDate: event.date,
            gaNotificationId: data.gaNotificationId || null,
            closesOn: data.closesOn || null, // (21 Oct 2026, 8.42, decision 115)
          });
        } else if (type === 'close' && poll.phase === 'offer' && data.confirmPoll) {
          // Manual close of an offer-phase poll that genuinely advanced to
          // Phase 2 (data.confirmPoll present) — same field pollScheduler.js
          // reads to distinguish this from the zero-offers dead end, where
          // data.confirmPoll is absent and closeOfferPollAndAdvance's
          // pollRoutes.js response has no further phase to announce.
          setWhatsappNotifyModal({
            type: 'poll_phase2_open',
            calendarName: currentCalendar?.name,
            eventTitle: event.title,
            eventDate: event.date,
            gaNotificationId: data.gaNotificationId || null,
            closesOn: data.closesOn || null, // (21 Oct 2026, 8.42, decisions 103, 113)
          });
        } else if (type === 'close' && poll.phase === 'confirm') {
          // (13 Sep 2026) Manual close of Phase 2 — offer to tell the group
          // final responses are ready to review, same pattern as the two
          // open-side triggers above.
          setWhatsappNotifyModal({
            type: 'poll_results_ready',
            calendarName: currentCalendar?.name,
            eventTitle: event.title,
            eventDate: event.date,
            gaNotificationId: data.gaNotificationId || null,
          });
        }
      } else {
        showStatus(data.error || `Failed to ${type === 'open' ? 'open' : 'close'} poll`, 'error');
      }
    } catch (err) {
      showStatus(`Failed to ${type === 'open' ? 'open' : 'close'} poll`, 'error');
    } finally {
      setPollActionInFlight(null);
      setPollActionConfirm(null);
    }
  };

  const handleRunScheduler = async () => {
    setIsRunningScheduler(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/run-poll-scheduler`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json',
        },
      });
      const data = await res.json();
      if (res.ok) {
        // (11 Oct 2026, decisions 98, 99) The run covers every calendar, so the
        // hosting-schedule counts are for all calendars that have one.
        showStatus(data.message || 'Scheduler run complete.', 'success');
        // (14 Sep 2026) A scheduler run can open/close polls and queue new
        // GA notifications (including reminders) — previously nothing
        // here refetched either, so the event cards' poll-status badges
        // and the bell panel's count/contents both stayed stale until a
        // hard refresh, even though the run itself had genuinely changed
        // things server-side.
        onRefreshEvents && onRefreshEvents();
        onRefreshNotifications && onRefreshNotifications();
      } else {
        showStatus(data.error || 'Failed to run poll scheduler', 'error');
      }
    } catch (err) {
      showStatus('Failed to run poll scheduler', 'error');
    } finally {
      setIsRunningScheduler(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50">
      {/* Toast Notification - Centered */}
      <StatusToast statusMessage={statusMessage} />

   <div className={`${currentCalendar?.is_deputy ? 'bg-red-600' : 'bg-white shadow-sm border-b'} sticky top-0 z-10`}>        <div className="max-w-4xl mx-auto px-4 py-2 sm:py-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div className="flex items-center gap-3">
              <img src="/Calendar_Hub_Logo.png" alt="Calendar Hub" className="w-14 h-14 object-contain shrink-0" />
                <div className="min-w-0">
                  {/* Calendar Switcher or title */}
                  {/* (28 Oct 2026, decision 154) Also shown when the person administers one
                      calendar and is a member of others. */}
                  {calendars.length > 1 || (calendars.length === 1 && memberCalendars.length > 0) ? (
                    // (2 Nov 2026, decision 170) The name wraps rather than being cut short.
                    <CalendarNamePicker
                      name={currentCalendar ? (currentCalendar.is_deputy ? `⚑ ${currentCalendar.name}` : currentCalendar.name) : ''}
                      value={currentCalendar?.id || ''}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v.startsWith('member:') && onChooseCalendar) onChooseCalendar(v);
                        else handleSwitchCalendar(v);
                      }}
                      textClass={`text-xl ${currentCalendar?.is_deputy ? 'text-white' : 'text-gray-800'}`}
                      chevronClass={currentCalendar?.is_deputy ? 'text-white' : 'text-gray-500'}
                      className="md:max-w-sm"
                    >
                        {memberCalendars.length > 0 ? (
                          <>
                            <optgroup label="Calendars you administer">
                              {calendars.map(cal => (
                                <option key={cal.id} value={cal.id}>{cal.is_deputy ? `⚑ ${cal.name}` : cal.name}</option>
                              ))}
                            </optgroup>
                            <optgroup label="Calendars you're a member of">
                              {memberCalendars.map(cal => (
                                <option key={`member:${cal.id}`} value={`member:${cal.id}`}>{cal.name}</option>
                              ))}
                            </optgroup>
                          </>
                        ) : calendars.map(cal => (
                          <option key={cal.id} value={cal.id}>{cal.is_deputy ? `⚑ ${cal.name}` : cal.name}</option>
                        ))}
                    </CalendarNamePicker>
                  ) : calendars.length === 1 ? (
                    <h1 className={`text-xl font-bold leading-tight break-words ${currentCalendar?.is_deputy ? 'text-white' : 'text-gray-800'}`}>{currentCalendar?.name || 'Calendar'}</h1>
                  ) : (
                    <h1 className="text-xl font-bold text-gray-800">Telematrix Calendar Hub</h1>
                  )}
                  {(currentCalendar?.is_deputy || calendars.length === 0) && (
                    <p className={`text-xs ${currentCalendar?.is_deputy ? 'text-red-100' : 'text-gray-500'}`}>
                      {currentCalendar?.is_deputy
                        ? `Standing-in for ${
                            currentCalendar.owner_preferred_name
                              || (currentCalendar.owner_first_name && currentCalendar.owner_last_name
                                    ? `${currentCalendar.owner_first_name} ${currentCalendar.owner_last_name}`
                                    : currentCalendar.owner_email)
                          }`
                        : 'Create your first calendar to get started'
                      }
                    </p>
                  )}
                  {calendars.length > 0 && userEmail && (
                    <p className={`text-xs ${currentCalendar?.is_deputy ? 'text-red-100' : 'text-gray-400'}`}>
                      Signed in as: {formatSignedInAs(preferredName, firstName, lastName, userEmail)}
                    </p>
                  )}
              </div>
            </div>
            {/* (24 Oct 2026, decision 133, amended the same day) Full labels on phones too:
                "New", "Rename" and "Delete" used to be hidden there, leaving three buttons
                reading "Calendar". On phones the buttons sit on two rows, Delete Calendar
                away from New and Rename: New Calendar, Rename Calendar, Settings | Delete
                Calendar, Sign Out, History, (Run Scheduler, Notifications,) Guide. The
                order-N classes apply below sm only; wider screens keep the order below. */}
            <div ref={topButtonsRef} className="flex flex-wrap gap-x-1.5 gap-y-0 sm:gap-2 items-center -mb-1.5 sm:mb-0">
              <button 
                // (8 Oct 2026, decision 40) Never disabled: pressing it says why when a new
                // calendar can't be created (was greyed out, with the reason only in a tooltip).
                onClick={() => {
                  if (!canCreateCalendar) {
                    // (27 Oct 2026, decision 148) "Regional Authority", as the Settings tab is named.
                    showStatus("You don't have permission to create a calendar. Please ask your Regional Authority.", 'error', 6000);
                    return;
                  }
                  const calendarLimit = userTier?.max_calendars;
                  const ownedCount = calendars.filter(c => !c.is_deputy).length;
                  if (calendarLimit !== null && calendarLimit !== undefined && ownedCount >= calendarLimit) {
                    showStatus(`You've reached your limit of ${calendarLimit} calendar${calendarLimit === 1 ? '' : 's'}.`, 'error', 6000);
                    return;
                  }
                  handleCreateNewCalendar();
                }}
                className="order-1 sm:order-none mb-1.5 sm:mb-0 flex items-center gap-0.5 sm:gap-1 bg-blue-600 text-white px-1.5 sm:px-2 py-1.5 rounded-lg hover:bg-blue-700 transition text-xs font-medium whitespace-nowrap"
                >
                <Plus className="w-3.5 h-3.5" />
                New Calendar
              </button>

              {!currentCalendar?.is_deputy && (
                <button 
                  // (11 Oct 2026, 8.37, decision 40) Never greyed out: with no calendar,
                  // pressing it says so (the reason used to be in the hover text only).
                  onClick={() => {
                    if (calendars.length === 0) { showStatus('You have no calendar to rename.', 'error'); return; }
                    setRenameCalendarInput(currentCalendar?.name || ''); setCalendarTimezone(currentCalendar?.timezone || 'Europe/London'); setShowRenameCalendar(true);
                  }}
                  className="order-2 sm:order-none mb-1.5 sm:mb-0 flex items-center gap-0.5 sm:gap-1 bg-blue-50 text-blue-600 px-1.5 sm:px-2 py-1.5 rounded-lg hover:bg-blue-100 transition text-xs font-medium border border-blue-200 whitespace-nowrap"
                >
                  <Pencil className="w-3.5 h-3.5" />
                  Rename Calendar
                </button>
              )}

              {!currentCalendar?.is_deputy && (
                <button 
                  // (11 Oct 2026, 8.37, decision 40) As Rename Calendar.
                  onClick={() => {
                    if (calendars.length === 0) { showStatus('You have no calendar to delete.', 'error'); return; }
                    setShowDeleteConfirm(true);
                  }}
                  className="order-5 sm:order-none mb-1.5 sm:mb-0 flex items-center gap-0.5 sm:gap-1 bg-red-50 text-red-600 px-1.5 sm:px-2 py-1.5 rounded-lg hover:bg-red-100 transition text-xs font-medium border border-red-200 whitespace-nowrap"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  Delete Calendar
                </button>
              )}

              {isAuthenticated() && (
                <button
                  onClick={() => {
                    clearAuthToken();
                    window.location.reload();
                  }}
                  className={`order-6 sm:order-none mb-1.5 sm:mb-0 px-1.5 sm:px-2 py-1.5 text-xs font-medium border rounded-lg transition whitespace-nowrap ${currentCalendar?.is_deputy ? 'text-white border-red-300 hover:bg-red-500' : 'text-red-600 hover:text-red-700 border-red-300 hover:bg-red-50'}`}
                >
                  Sign Out
                </button>
              )}
              {/* (11 Sep 2026) Settings is now available to an active deputy too —
                  is_deputy already means "currently active holder via stand-in",
                  not mere eligibility, per the 11 Sep is_deputy fix. SettingsModal
                  itself restricts a deputy to Profile/Preferences/Poll Settings
                  via restrictToDeputyTabs below; Calendar Authority, Members,
                  Subscription etc. stay owner-only regardless. */}
              <button
                onClick={() => setShowSettings(true)}
                className="order-3 sm:order-none mb-1.5 sm:mb-0 flex items-center gap-0.5 sm:gap-1 px-1.5 sm:px-2 py-1.5 text-gray-600 hover:text-gray-900 text-xs whitespace-nowrap border border-gray-300 rounded-lg hover:bg-gray-50 transition font-medium"
              >
                <Settings className="w-3.5 h-3.5" />
                Settings
              </button>
              {/* (30 Oct 2026, decision 160, replacing 133's fixed break after Settings) Phones only:
                  useBalancedRows shows this break where it splits the buttons into two rows. */}
              <div data-row-break aria-hidden="true" className="hidden h-0" />
              <div data-row-break aria-hidden="true" className="hidden h-0" />
              {/* (9 Oct 2026, 8.19/8.20, decision 53) The History page, on calendars
                  that keep a calendar history. */}
              {keepsHistory && (
                <button
                  // (decision 58) While History is showing, this shows as selected and does
                  // nothing; Back on the History page returns to the dashboard.
                  onClick={() => setShowHistory(true)}
                  aria-current={showHistory ? 'page' : undefined}
                  className={`order-7 sm:order-none mb-1.5 sm:mb-0 flex items-center gap-0.5 sm:gap-1 px-1.5 sm:px-2 py-1.5 text-xs whitespace-nowrap border rounded-lg transition font-medium ${showHistory ? 'bg-blue-50 border-blue-300 text-blue-700 cursor-default' : 'text-gray-600 hover:text-gray-900 border-gray-300 hover:bg-gray-50'}`}
                >
                  <BookOpen className="w-3.5 h-3.5" />
                  History
                </button>
              )}
              {/* (11 Oct 2026, decision 99) For the System Authority only: a run covers every
                  calendar in Calendar Hub, so it is a testing tool, not an administrator's. */}
              {isSA && (
                <button
                  onClick={() => { if (!isRunningScheduler) setConfirmRunScheduler(true); }}
                  className="order-8 sm:order-none mb-1.5 sm:mb-0 flex items-center gap-0.5 sm:gap-1 px-1.5 sm:px-2 py-1.5 text-gray-600 hover:text-gray-900 text-xs whitespace-nowrap border border-gray-300 rounded-lg hover:bg-gray-50 transition font-medium"
                >
                  <RefreshCw className={`w-3.5 h-3.5 ${isRunningScheduler ? 'animate-spin' : ''}`} />
                  Run Scheduler
                </button>
              )}
              {/* (11 Oct 2026, 8.5, decision 45) Shown to a stand-in standing in too. */}
              {(pendingNotifications.length + hostBoardTasks.length) > 0 && (
                <div className="order-9 sm:order-none mb-1.5 sm:mb-0 relative">
                  <button
                    onClick={() => setShowNotificationsPanel(prev => !prev)}
                    className="relative flex items-center gap-1 px-2 py-1.5 text-gray-600 hover:text-gray-900 text-xs whitespace-nowrap border border-gray-300 rounded-lg hover:bg-gray-50 transition font-medium"
                    title="Pending notifications"
                  >
                    <Bell className="w-3.5 h-3.5" />
                    <span className="hidden sm:inline">Notifications</span>
                    <span className="absolute -top-1.5 -right-1.5 bg-red-600 text-white text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
                      {pendingNotifications.length + hostBoardTasks.length}
                    </span>
                  </button>

                  {showNotificationsPanel && (
                    /* (14 Sep 2026) Below sm: this used to be `absolute right-0`,
                       anchored to the small bell-button wrapper rather than the
                       screen — on a narrow phone that wrapper can sit well left
                       of center, so a fixed 320px-wide panel anchored to its
                       right edge ran off the left side of the viewport entirely.
                       Fixed positioning + explicit inset values pins it to the
                       viewport itself instead, so it's always fully on-screen.
                       sm+ (desktop) keeps the original button-anchored behavior,
                       already tested and unchanged. */
                    <div className="fixed left-3 right-3 top-24 sm:absolute sm:left-auto sm:right-0 sm:top-auto sm:mt-2 w-auto sm:w-80 bg-white rounded-lg shadow-lg border border-gray-200 z-20 max-h-96 overflow-y-auto">
                      <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
                        <h3 className="text-sm font-bold text-gray-900">Notifications</h3>
                        <button onClick={() => setShowNotificationsPanel(false)} className="text-gray-400 hover:text-gray-600">
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                      {/* (20 Sep 2026) Host schedule board tasks. Requests to the chosen host are sent by
                          the GA herself: the WhatsApp message opens with no contact chosen, so she picks the
                          person in WhatsApp; "Email instead" is for a host who is not on WhatsApp. */}
                      {hostBoardTasks.map(task => (
                        <div key={task.id} className="px-4 py-3 border-b border-gray-100 last:border-b-0">
                          <p className="text-xs font-semibold text-gray-800 mb-1">{currentCalendar?.name} — Hosting schedule</p>
                          <p className="text-xs text-gray-600 mb-2">{task.summary}</p>
                          {task.kind === 'reconfirm_request' ? (
                            <>
                              <p className="text-xs text-blue-600 mb-2">
                                The message is ready. In WhatsApp, choose the person to send it to.
                              </p>
                              <div className="flex gap-2">
                                <button
                                  onClick={() => handleSendBoardTask(task)}
                                  disabled={resolvingBoardTaskId === task.id}
                                  className="flex-1 bg-green-600 text-white text-xs font-medium py-1.5 rounded-lg hover:bg-green-700 transition disabled:opacity-50"
                                >
                                  Send via WhatsApp
                                </button>
                                <button
                                  onClick={() => handleResolveBoardTask(task, 'skipped')}
                                  disabled={resolvingBoardTaskId === task.id}
                                  className="flex-1 border border-gray-300 text-gray-600 text-xs font-medium py-1.5 rounded-lg hover:bg-gray-50 transition disabled:opacity-50"
                                >
                                  Dismiss
                                </button>
                              </div>
                              {confirmEmailTaskId === task.id ? (
                                <div className="flex items-center gap-2 mt-2 text-xs text-gray-600">
                                  <span>Email the host now?</span>
                                  <button
                                    onClick={() => { setConfirmEmailTaskId(null); handleEmailBoardTask(task); }}
                                    disabled={resolvingBoardTaskId === task.id}
                                    className="px-2 py-1 rounded border border-blue-300 text-blue-700 hover:bg-blue-50 disabled:opacity-50"
                                  >
                                    Yes, email
                                  </button>
                                  <button onClick={() => setConfirmEmailTaskId(null)} className="px-2 py-1 rounded border border-gray-300">
                                    No
                                  </button>
                                </div>
                              ) : (
                                <button
                                  onClick={() => setConfirmEmailTaskId(task.id)}
                                  disabled={resolvingBoardTaskId === task.id}
                                  className="mt-2 text-xs text-gray-500 hover:text-gray-800 underline disabled:opacity-50"
                                >
                                  Host not on WhatsApp? Email instead
                                </button>
                              )}
                            </>
                          ) : (
                            <button
                              onClick={() => handleResolveBoardTask(task, 'dismissed')}
                              disabled={resolvingBoardTaskId === task.id}
                              className="w-full border border-gray-300 text-gray-600 text-xs font-medium py-1.5 rounded-lg hover:bg-gray-50 transition disabled:opacity-50"
                            >
                              Dismiss
                            </button>
                          )}
                        </div>
                      ))}
                      {pendingNotifications.map(n => (
                        <div key={n.id} className="px-4 py-3 border-b border-gray-100 last:border-b-0">
                          <p className="text-xs font-semibold text-gray-800 mb-1">
                            {n.calendars?.name} — {n.events?.event_date ? formatIsoDateToPref(n.events.event_date, userTier?.preferred_date_format) : ''}
                          </p>
                          {/* (23 Sep 2026) *Event name* (WhatsApp bold) shows in blue here;
                              line breaks kept so the link sits on its own line. */}
                          <p className="text-xs text-gray-600 mb-2 whitespace-pre-line">
                            {n.message_text
                              .replace(/\d{4}-\d{2}-\d{2}/g, (d) => formatIsoDateToPref(d, userTier?.preferred_date_format))
                              .split(/(\*[^*]+\*)/g)
                              .map((part, i) => (/^\*[^*]+\*$/.test(part)
                                ? <span key={i} className="font-semibold text-blue-600">{part.slice(1, -1)}</span>
                                : <React.Fragment key={i}>{part}</React.Fragment>))}
                          </p>
                          {/* (14 Sep 2026) Every trigger_reason except
                              phase1_closed_zero_offers already emails
                              every member directly (see pollScheduler.js's
                              notifyMembers) at the same time this
                              notification is queued — without this note,
                              a GA who sends via WhatsApp too might repeat
                              themselves to members who already got the
                              email, which reads as irritating rather than
                              helpful. This is a hardcoded mirror of
                              notifyMembers' own allow-list, not a new
                              flag from the backend — the two need to stay
                              in sync if that list ever changes. */}
                          {/* (27 Sep 2026) WhatsApp first (Neil): the group post is what
                              reaches everyone, including members who chose no emails.
                              Doesn't claim members were emailed: after a manual
                              open/close the GA may have chosen not to email them. */}
                          {/* (31 Oct 2026, decision 164) Advice-only notices: no "post this" note. */}
                          {!['phase1_closed_zero_offers', 'phase1_closed_event_day'].includes(n.trigger_reason) && (
                            <p className="text-xs text-blue-600 mb-2">
                              Post this to the group so everyone sees it, including members who don't receive emails.
                            </p>
                          )}
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleSendPollNotification(n)}
                              disabled={resolvingNotificationId === n.id}
                              className="flex-1 bg-green-600 text-white text-xs font-medium py-1.5 rounded-lg hover:bg-green-700 transition disabled:opacity-50"
                            >
                              Send via WhatsApp
                            </button>
                            <button
                              onClick={() => resolveNotification(n.id, 'skipped')}
                              disabled={resolvingNotificationId === n.id}
                              className="flex-1 border border-gray-300 text-gray-600 text-xs font-medium py-1.5 rounded-lg hover:bg-gray-50 transition disabled:opacity-50"
                            >
                              Dismiss
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              <button
                onClick={() => setShowUserGuide(true)}
                className="order-10 sm:order-none mb-1.5 sm:mb-0 flex items-center gap-0.5 sm:gap-1 text-gray-700 hover:text-blue-600 border border-gray-300 px-1.5 sm:px-2 py-1.5 rounded-lg transition text-xs font-medium whitespace-nowrap"
                title="User Guide"
              >
                <Info className="w-4 h-4" />
                Guide
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* (9 Oct 2026, 8.19/8.20, decision 58) History, in place of the dashboard's content */}
      {showHistory && keepsHistory && (
        <HistoryPanel
          calendar={{ id: currentCalendar.id, name: currentCalendar.name }}
          apiBaseUrl={API_BASE_URL}
          authToken={localStorage.getItem('auth_token')}
          formatEventDate={(d) => formatIsoDateToPref(d, userTier?.preferred_date_format)}
          onStatusMessage={showStatus}
          onBack={() => setShowHistory(false)}
        />
      )}
      {/* (10 Oct 2026, decision 77) While History is showing, only the dashboard's own
          content is hidden. The pop-up windows further down (Rename Calendar, Delete
          Calendar, Settings, Guide and the rest) stay outside the hidden part, so the
          banner's buttons open them over the History page; closing one leaves History
          showing. (Hiding the whole block had opened them invisibly, to appear only
          after Back.) */}
      <div className={showHistory && keepsHistory ? '' : 'max-w-4xl mx-auto px-4 py-6'}>
        <div className={showHistory && keepsHistory ? 'hidden' : ''}>
        <div className="bg-white rounded-xl shadow-sm p-4 mb-6">
          <div className="flex items-center justify-between text-sm">
            {/* Left: Calendar count */}
            <div className="flex items-center gap-2">
              {(() => {
                const calendarLimit = userTier?.max_calendars;
                const calendarCount = calendars.length;
                const calendarPercent = calendarLimit ? (calendarCount / calendarLimit) * 100 : 0;
                const calendarColor = calendarPercent >= 100 ? 'text-red-600 font-semibold' : 
                                     calendarPercent >= 80 ? 'text-orange-500 font-semibold' : 
                                     'text-gray-600';
                
                return (
                  <>
                    <Calendar className="w-4 h-4 text-blue-600" />
                    <span className={calendarColor}>
                      {/* (11 Oct 2026, 8.37) No "(s)" plurals. */}
                      {calendarLimit
                        ? `${calendarCount}/${calendarLimit} ${calendarLimit === 1 ? 'calendar' : 'calendars'}`
                        : `${calendarCount} ${calendarCount === 1 ? 'calendar' : 'calendars'}`}
                    </span>
                  </>
                );
              })()}
            </div>

            {/* Right: Event count */}
            <div className="flex items-center gap-2">
              {/* (19 Sep 2026) Plain count. The old "N/limit" display keyed off the
                  tier's max_events_per_calendar, which is null for every live tier; the
                  real, server-side limits live on the calendar now. */}
              <CalendarDays className="w-4 h-4 text-blue-600" />
              <span className="text-gray-600">{`${events.length} ${events.length === 1 ? 'event' : 'events'}`}</span>
            </div>
          </div>
        </div>

        {/* (19 Sep 2026) Client-side event gate removed. It keyed off the tier's
            max_events_per_calendar (null for every live tier, so it never fired) and
            its tooltip claimed a "Free tier" limit that does not exist. Limits are
            enforced server-side per calendar (403 EVENT_LIMIT / OUTSIDE_PERIOD); the
            Tier Error Modal shows the server's message. */}
        {!showAddEvent && !showEditEvent && (
          <button
            onClick={() => setShowAddEvent(true)}
            className="w-full rounded-xl shadow-sm p-4 mb-6 transition flex items-center justify-center gap-2 font-semibold bg-blue-600 text-white hover:bg-blue-700"
            title="Add a new event"
          >
            <Plus className="w-5 h-5" />
            <span>Add Event</span>
          </button>
        )}

        {showAddEvent && (
          <div ref={editEventRef} className="bg-white rounded-xl shadow-lg p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-800">{editingEvent ? 'Edit Event' : 'New Event'}</h2>
              <button onClick={() => editingEvent ? cancelEdit() : setShowAddEvent(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* (20 Sep 2026) Opened from a confirmed host-board month */}
            {pendingHostSlotId && (
              <p className="text-sm text-blue-800 bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                Filled in from the hosting schedule. Adjust anything you like, then click Add Event.
              </p>
            )}

            
            <div className="space-y-4">
              <input type="text" placeholder="Event title *" value={newEvent.title} onChange={(e) => setNewEvent({...newEvent, title: e.target.value})} className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none" />
              
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {renderDateBox()}
                <select value={newEvent.time} onChange={(e) => {
                    const t = e.target.value;
                    setNewEvent(prev => ({ ...prev, time: t, endTime: getEndTime(t) }));
                  }} className="px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none">
                  <option value="">Start time</option>
                  {Array.from({ length: 96 }, (_, i) => {
                    const h = Math.floor(i / 4);
                    const m = (i % 4) * 15;
                    const t = String(h).padStart(2, '0') + ':' + String(m).padStart(2, '0');
                    return <option key={t} value={t}>{t}</option>;
                  })}
                </select>
                <select value={newEvent.endTime} onChange={(e) => setNewEvent(prev => ({ ...prev, endTime: e.target.value }))} className="px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none">
                  <option value="">End time</option>
                  {Array.from({ length: 96 }, (_, i) => {
                    const h = Math.floor(i / 4);
                    const m = (i % 4) * 15;
                    const t = String(h).padStart(2, '0') + ':' + String(m).padStart(2, '0');
                    if (newEvent.time && t <= newEvent.time) return null;
                    return <option key={t} value={t}>{t}</option>;
                  })}
                </select>
              </div>
              
              <div className="relative">
                <span className="absolute left-3 top-0 -translate-y-1/2 bg-white px-1 text-xs text-gray-500">Location</span>
                <input type="text" placeholder="" value={newEvent.location} onChange={(e) => setNewEvent({...newEvent, location: e.target.value})} className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none" />
                {locationHint}
              </div>
              
              <div className="relative">
                <span className="absolute left-3 top-0 -translate-y-1/2 bg-white px-1 text-xs text-gray-500">Notes</span>
                <textarea placeholder="" value={newEvent.description} onChange={(e) => setNewEvent({...newEvent, description: e.target.value})} className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none h-20 resize-none" />
              </div>
              {renderIndexBox()}
              

              {/* (30 Sep 2026, H17) Event host, for the map and Directions */}
              {renderHostPicker()}

              {/* Timezone override — collapsed by default */}
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <label className="flex items-center gap-2 px-4 py-3 cursor-pointer select-none bg-gray-50 hover:bg-gray-100">
                  <input
                    type="checkbox"
                    checked={eventUseCustomTz}
                    onChange={(e) => {
                      setEventUseCustomTz(e.target.checked);
                      if (!e.target.checked) { setEventDiffEndTz(false); setEventStartTz(''); setEventEndTz(''); }
                      else if (!eventStartTz) setEventStartTz(calendarTimezone);
                    }}
                    className="w-4 h-4"
                  />
                  <span className="text-sm text-gray-700">Set custom timezone for this event</span>
                </label>

                {eventUseCustomTz && (
                  <div className="px-4 pb-4 pt-2 space-y-3 bg-white">
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Start timezone</label>
                      <select
                        value={eventStartTz || calendarTimezone}
                        onChange={(e) => setEventStartTz(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:border-blue-500 focus:outline-none"
                      >
                        {TIMEZONES.map(tz => (
                          <option key={tz.value} value={tz.value}>{tz.label}</option>
                        ))}
                      </select>
                    </div>

                    <label className="flex items-center gap-2 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={eventDiffEndTz}
                        onChange={(e) => {
                          setEventDiffEndTz(e.target.checked);
                          if (!e.target.checked) setEventEndTz('');
                          else if (!eventEndTz) setEventEndTz(eventStartTz || calendarTimezone);
                        }}
                        className="w-4 h-4"
                      />
                      <span className="text-xs text-gray-600">End time is in a different timezone</span>
                    </label>

                    {eventDiffEndTz && (
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">End timezone</label>
                        <select
                          value={eventEndTz || eventStartTz || calendarTimezone}
                          onChange={(e) => setEventEndTz(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:border-blue-500 focus:outline-none"
                        >
                          {TIMEZONES.map(tz => (
                            <option key={tz.value} value={tz.value}>{tz.label}</option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* (6 Oct 2026, H18 Stage 2) The event form is the administrator's single
                  starting point: on a calendar with a hosting schedule a date can also go
                  on the schedule only, with no event or poll yet. */}
              {usesSchedule && !editingEvent && !pendingHostSlotId && (
                <label className="flex items-start gap-2 p-3 rounded-lg border border-gray-200 bg-gray-50 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={!!newEvent.scheduleOnly}
                    onChange={(e) => setNewEvent({ ...newEvent, scheduleOnly: e.target.checked })}
                    className="w-4 h-4 mt-0.5"
                  />
                  <span className="text-sm text-gray-700">
                    Add it to the hosting schedule <span className="font-bold">only</span> for now
                    {newEvent.scheduleOnly && (
                      <span className="block text-xs text-gray-500 mt-1">
                        The date, host and notes go on the hosting schedule as "Day proposed"; members can see it and offer to host. No event or poll yet: create the event from its row when you're ready. The title, times and location are not kept.
                      </span>
                    )}
                  </span>
                </label>
              )}

              <button
                onClick={editingEvent ? saveEditEvent : addEvent}
                disabled={(!newEvent.scheduleOnly && !newEvent.title) || !newEvent.date || isAddingEvent}
                className="w-full bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 transition disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                {isAddingEvent
                  ? (editingEvent ? 'Updating...' : 'Adding...')
                  : (editingEvent ? 'Update Event' : (newEvent.scheduleOnly && usesSchedule && !pendingHostSlotId ? 'Add to hosting schedule' : 'Add Event'))}
              </button>
            </div>
          </div>
        )}

        {showEditEvent && (
          <div ref={editEventRef} className="bg-white rounded-xl shadow-lg p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-800">Edit Event</h2>
              <button onClick={cancelEdit} className="text-gray-400 hover:text-gray-600">
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="space-y-4">
              <input type="text" placeholder="Event title *" value={newEvent.title} onChange={(e) => setNewEvent({...newEvent, title: e.target.value})} className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none" />
              
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {renderDateBox()}
                <select value={newEvent.time} onChange={(e) => {
                    const t = e.target.value;
                    setNewEvent(prev => ({ ...prev, time: t, endTime: getEndTime(t) }));
                  }} className="px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none">
                  <option value="">Start time</option>
                  {Array.from({ length: 96 }, (_, i) => {
                    const h = Math.floor(i / 4);
                    const m = (i % 4) * 15;
                    const t = String(h).padStart(2, '0') + ':' + String(m).padStart(2, '0');
                    return <option key={t} value={t}>{t}</option>;
                  })}
                </select>
                <select value={newEvent.endTime} onChange={(e) => setNewEvent(prev => ({ ...prev, endTime: e.target.value }))} className="px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none">
                  <option value="">End time</option>
                  {Array.from({ length: 96 }, (_, i) => {
                    const h = Math.floor(i / 4);
                    const m = (i % 4) * 15;
                    const t = String(h).padStart(2, '0') + ':' + String(m).padStart(2, '0');
                    if (newEvent.time && t <= newEvent.time) return null;
                    return <option key={t} value={t}>{t}</option>;
                  })}
                </select>
              </div>
              
              <div className="relative">
                <span className="absolute left-3 top-0 -translate-y-1/2 bg-white px-1 text-xs text-gray-500">Location</span>
                <input type="text" placeholder="" value={newEvent.location} onChange={(e) => setNewEvent({...newEvent, location: e.target.value})} className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none" />
                {locationHint}
              </div>
              
              <div className="relative">
                <span className="absolute left-3 top-0 -translate-y-1/2 bg-white px-1 text-xs text-gray-500">Notes</span>
                <textarea placeholder="" value={newEvent.description} onChange={(e) => setNewEvent({...newEvent, description: e.target.value})} className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none min-h-[100px]" />
              </div>
              {renderIndexBox()}
              

              {/* (30 Sep 2026, H17) Event host, for the map and Directions */}
              {renderHostPicker()}

              {/* Timezone override — collapsed by default */}
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <label className="flex items-center gap-2 px-4 py-3 cursor-pointer select-none bg-gray-50 hover:bg-gray-100">
                  <input
                    type="checkbox"
                    checked={eventUseCustomTz}
                    onChange={(e) => {
                      setEventUseCustomTz(e.target.checked);
                      if (!e.target.checked) { setEventDiffEndTz(false); setEventStartTz(''); setEventEndTz(''); }
                      else if (!eventStartTz) setEventStartTz(calendarTimezone);
                    }}
                    className="w-4 h-4"
                  />
                  <span className="text-sm text-gray-700">Set custom timezone for this event</span>
                </label>

                {eventUseCustomTz && (
                  <div className="px-4 pb-4 pt-2 space-y-3 bg-white">
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Start timezone</label>
                      <select
                        value={eventStartTz || calendarTimezone}
                        onChange={(e) => setEventStartTz(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:border-blue-500 focus:outline-none"
                      >
                        {TIMEZONES.map(tz => (
                          <option key={tz.value} value={tz.value}>{tz.label}</option>
                        ))}
                      </select>
                    </div>

                    <label className="flex items-center gap-2 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={eventDiffEndTz}
                        onChange={(e) => {
                          setEventDiffEndTz(e.target.checked);
                          if (!e.target.checked) setEventEndTz('');
                          else if (!eventEndTz) setEventEndTz(eventStartTz || calendarTimezone);
                        }}
                        className="w-4 h-4"
                      />
                      <span className="text-xs text-gray-600">End time is in a different timezone</span>
                    </label>

                    {eventDiffEndTz && (
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">End timezone</label>
                        <select
                          value={eventEndTz || eventStartTz || calendarTimezone}
                          onChange={(e) => setEventEndTz(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:border-blue-500 focus:outline-none"
                        >
                          {TIMEZONES.map(tz => (
                            <option key={tz.value} value={tz.value}>{tz.label}</option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <button 
                onClick={saveEditEvent} 
                disabled={!newEvent.title || !newEvent.date || isAddingEvent}
                className="w-full bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 transition disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                {isAddingEvent ? 'Updating...' : 'Update Event'}
              </button>
            </div>
          </div>
        )}

        {/* (23 Oct 2026, decision 126) On a calendar with a hosting schedule, Add Event (and the
            event form it opens) now comes first, then the schedule, then the events: the
            schedule can run to a dozen rows, which pushed Add Event a long scroll down.
            Calendars without a schedule (Hot Club) are unchanged: HostBoard renders nothing. */}
        {/* (20 Sep 2026) Host schedule board — one row per month (day + host). Renders
            the on/off prompt, or the board, or nothing, depending on the calendar's
            host_board_enabled flag and the caller's authority (decided inside). */}
        {currentCalendar && (
          <HostBoard
            calendar={currentCalendar}
            apiBaseUrl={API_BASE_URL}
            showStatus={showStatus}
            onCreateEvent={handleStartEventFromBoard}
            onAnnounce={handleAnnounceHostBoard}
            refreshToken={hostBoardVersion}
            onBoardChanged={() => {
              // (5 Oct 2026, H18 Stage 1) A host changed on the schedule can change an event's Location.
              if (typeof handleHostBoardChanged === 'function') handleHostBoardChanged();
              if (typeof onRefreshEvents === 'function') onRefreshEvents();
            }}
            onLinkedEventsChange={setBoardEventInfo}
            onCancelEventForMonth={(eventUid, note) => deleteEvent({ eventId: eventUid, monthOutcome: 'off', monthNote: note })}
          />
        )}

        {sortedEvents.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm p-12 text-center">
            <Calendar className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-600 mb-2">No events yet</h3>
            <p className="text-gray-500">Add your first event to get started!</p>
          </div>
        ) : (
          <div className="space-y-4 mb-4">
            {sortedEvents.map((event) => {
              const eventDate = new Date(event.date);
              const isUpcoming = eventDate >= new Date();
              // (26 Sep 2026) Poll can be opened only for an event AFTER today
              // (local date). Compared as 'YYYY-MM-DD' strings: new Date('YYYY-MM-DD')
              // is UTC midnight, which would misjudge "today" either side of it.
              const nowLocal = new Date();
              const todayStr = `${nowLocal.getFullYear()}-${String(nowLocal.getMonth() + 1).padStart(2, '0')}-${String(nowLocal.getDate()).padStart(2, '0')}`;
              const canOpenPoll = typeof event.date === 'string' && event.date.slice(0, 10) > todayStr;
              
              return (
                // (24 Sep 2026) data-event-uid: lets App.js bring this card back into view
                // on return from the poll or results page (see rememberScroll there).
                <div key={event.id} data-event-uid={event.eventUid} className="bg-white rounded-xl shadow-sm p-6 hover:shadow-md transition border-l-4 border-blue-500">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="text-xl font-bold text-gray-800">{event.title}</h3>
                      </div>
                      
                      <div className="space-y-2 text-gray-600 text-sm">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-blue-600" />
                          {/* (24 Oct 2026, 8.40a, decision 100) The person's Date Format, with the
                              day of the week unless they untick it: "Sunday 8-Nov-2026" (was always
                              "Sunday, November 8, 2026"). */}
                          <span>{formatEventDateByPref(event.date, userTier?.preferred_date_format, showWeekday)}</span>
                        </div>
                        
                        {event.time && (
                          <div className="flex items-center gap-2">
                            <Clock className="w-4 h-4 text-blue-600" />
                            <span>{formatTime(event.time)}{event.endTime ? ' - ' + formatTime(event.endTime) : ''}{(() => { const abbr = getTzAbbr(event.start_timezone || calendarTimezone, event.date); return abbr ? ' ' + abbr : ''; })()}</span>
                          </div>
                        )}
                        
                        {/* (1 Oct 2026, L1) One Location line, the same on the
                            administrator's and members' cards: pin, the event's
                            Location text, then "Directions" when the host has a
                            saved location (H17). Directions alone if there is no
                            Location text. */}
                        {(event.location || event.hostLocation) && (
                          <div className="flex items-center gap-2 flex-wrap">
                            <MapPin className="w-4 h-4 text-blue-600 flex-shrink-0" />
                            {event.location && <span>{event.location}</span>}
                            {event.location && event.hostLocation && <span className="text-gray-400">·</span>}
                            {event.hostLocation && (
                              <a
                                href={/iPhone|iPad|iPod|Macintosh/.test(navigator.userAgent || '')
                                  ? `https://maps.apple.com/?daddr=${event.hostLocation.lat},${event.hostLocation.lng}`
                                  : `https://www.google.com/maps/dir/?api=1&destination=${event.hostLocation.lat},${event.hostLocation.lng}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="font-medium text-blue-700 hover:underline"
                              >
                                Directions
                              </a>
                            )}
                          </div>
                        )}
                      </div>
                      
                      {event.description && (
                        <p className="mt-3 text-gray-600 text-sm whitespace-pre-line">Notes: {event.description}</p>
                      )}

                      {/* (10 Sep 2026) Poll status + manual controls — DashboardStep
                          previously had zero poll visibility at all. Open Phase 1
                          shows only when no poll exists yet; Close Poll Now shows
                          only when one is currently open, for either phase. Both
                          require confirmation via pollActionConfirm before firing.
                          View Results shows whenever ANY poll has ever existed
                          (open or historical) — the GA needs to see current
                          standing at any phase, not only once the whole cycle
                          finishes, per feedback the "Poll cycle complete" fix
                          alone didn't address. */}
                      <div className="mt-3 flex flex-wrap items-center gap-2">
                        {event.poll ? (
                          <>
                            <span className={`text-xs font-medium px-2 py-1 rounded-full whitespace-nowrap ${
                              event.poll.phase === 'offer' ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'
                            }`}>
                              {event.poll.phase === 'offer' ? 'Phase 1 (offers) now open' : 'Phase 2 (confirm) now open'}
                            </span>
                            <button
                              type="button"
                              onClick={() => setPollActionConfirm({ type: 'close', event, poll: event.poll })}
                              disabled={pollActionInFlight === event.id}
                              className="text-xs font-medium px-2 py-1 rounded-full whitespace-nowrap border border-red-300 text-red-600 hover:bg-red-50 transition disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {event.poll.phase === 'offer' ? 'Close Poll Phase 1 Now' : 'Close Poll Phase 2 Now'}
                            </button>
                            {/* (12 Sep 2026) Lets the GA answer this poll as
                                themselves via the normal, non-proxy PollPage
                                flow — same screen and endpoints a plain
                                member uses. Added because the only existing
                                route to a genuine self-answer was via
                                Members -> proxy-answer, which is built for
                                answering ON BEHALF OF someone else and
                                carries "Answering on behalf of..." framing
                                that's misleading for a GA answering for
                                themselves. Also intended to show new GAs the
                                same sequence an ordinary member goes
                                through, useful when walking a
                                less-technical member through it by phone. */}
                            {onAnswerPollAsSelf && (
                              <button
                                type="button"
                                onClick={() => onAnswerPollAsSelf(event.eventUid)}
                                className="text-xs font-medium px-2 py-1 rounded-full whitespace-nowrap border border-green-300 text-green-700 hover:bg-green-50 transition"
                              >
                                Answer as yourself
                              </button>
                            )}
                          </>
                        ) : event.hasPollHistory ? (
                          // (10 Sep 2026) Poll cycle already fully completed (offer
                          // closed, confirm closed or never started due to zero
                          // offers) — deliberately no button. Offering "Open Phase
                          // 1" here looked identical to "never polled" and would
                          // have let a GA accidentally restart a whole new poll
                          // cycle on an event whose attendance/dishes are already
                          // confirmed (openOfferPoll only guards against a
                          // duplicate OPEN offer poll, not a completed one).
                          <span className="text-xs font-medium px-2 py-1 rounded-full whitespace-nowrap bg-gray-100 text-gray-500">
                            Poll cycle complete
                          </span>
                        ) : canOpenPoll ? (
                          // (26 Sep 2026) Not shown for a past or same-day event: opening
                          // a poll for a meeting that has happened would email every member.
                          <>
                          {/* (23 Oct 2026, 8.43, decision 112) When the scheduler will open
                              Phase 1 by itself; nothing when it won't (abandoned, or Poll
                              Settings don't open it "days before"). */}
                          {/* (24 Oct 2026, decision 131) Pressing it opens the Poll dates window
                              for this event as it stands: keep, choose different dates, or
                              abandon the poll. An abandoned poll shows "No poll will open by
                              itself ›" instead, which offers to let it open by itself again. */}
                          {event.pollOpensOn && (
                            <button
                              type="button"
                              onClick={() => onOpenPollDates && onOpenPollDates(event)}
                              className="text-xs font-medium px-2 py-1 rounded-full whitespace-nowrap bg-gray-100 text-gray-600 hover:bg-gray-200 transition"
                            >
                              Poll phase 1 (offers) opens {fullDayDate(event.pollOpensOn)} {'\u203A'}
                            </button>
                          )}
                          {!event.pollOpensOn && event.pollSuppressed && onOpenPollDates && (
                            <button
                              type="button"
                              onClick={() => onOpenPollDates(event)}
                              className="text-xs font-medium px-2 py-1 rounded-full whitespace-nowrap bg-gray-100 text-gray-600 hover:bg-gray-200 transition"
                            >
                              No poll will open by itself {'\u203A'}
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => setPollActionConfirm({ type: 'open', event })}
                            disabled={pollActionInFlight === event.id}
                            className="text-xs font-medium px-2 py-1 rounded-full whitespace-nowrap border border-gray-300 text-gray-600 hover:bg-gray-50 transition disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Open Phase 1 (offers)
                          </button>
                          </>
                        ) : null}
                        {(event.poll || event.hasPollHistory) && onViewPollResults && (
                          <button
                            type="button"
                            onClick={() => onViewPollResults(event.eventUid)}
                            className="text-xs font-medium px-2 py-1 rounded-full whitespace-nowrap border border-blue-300 text-blue-600 hover:bg-blue-50 transition"
                          >
                            View Results
                          </button>
                        )}
                      </div>
                      {/* (26 Oct 2026, decision 145) The event's Event Administrator (its
                          confirmed host, on a calendar with "Confirmed hosts are Event
                          Administrators" ticked), with the administrator's override. */}
                      {event.eventAdmin && (
                        <div className="mt-2 flex flex-wrap items-center gap-2">
                          <span className="text-xs text-gray-600">
                            {event.eventAdmin.withdrawn
                              ? `Host: ${event.eventAdmin.name || 'the host'} (event administrator rights withdrawn)`
                              : `Host: ${event.eventAdmin.name || 'the host'} (event administrator)`}
                          </span>
                          <button
                            type="button"
                            onClick={() => setEaConfirm({ event, withdraw: !event.eventAdmin.withdrawn })}
                            className="text-xs font-medium px-2 py-1 rounded-full whitespace-nowrap border border-gray-300 text-gray-600 hover:bg-gray-50 transition"
                          >
                            {event.eventAdmin.withdrawn ? 'Restore event administrator rights' : 'Withdraw event administrator rights'}
                          </button>
                        </div>
                      )}
                    </div>
                    
                    <div className="flex flex-col items-end gap-1.5">
                      {isUpcoming && (
                        <span className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-xs font-semibold">Upcoming</span>
                      )}
                      {/* (6 Oct 2026, H18 Stage 2) Replaces "Not on hosting schedule". */}
                      {currentCalendar?.host_board_enabled && boardEventInfo && boardEventInfo.oneOffUids.includes(event.id) && (
                        <span className="bg-gray-100 text-gray-600 px-3 py-1 rounded-full text-xs font-semibold text-right">
                          One-off: members don't see this on the schedule
                        </span>
                      )}
                    </div>
                  </div>
                  {event.waLog && event.waLog.length > 0 && (
                    <div className="mt-2 space-y-1">
                      {event.waLog.map((entry, idx) => (
                        <div key={idx} className="flex items-center gap-1.5 text-xs text-green-700">
                          <svg viewBox="0 0 24 24" className="w-3 h-3 flex-shrink-0 fill-green-500" xmlns="http://www.w3.org/2000/svg">
                            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
                            <path d="M12 0C5.373 0 0 5.373 0 12c0 2.127.558 4.122 1.533 5.854L.057 23.48a.75.75 0 0 0 .92.92l5.687-1.476A11.945 11.945 0 0 0 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.818a9.823 9.823 0 0 1-5.006-1.368l-.36-.214-3.714.964.991-3.624-.235-.373A9.818 9.818 0 0 1 2.182 12C2.182 6.57 6.57 2.182 12 2.182c5.43 0 9.818 4.388 9.818 9.818 0 5.43-4.388 9.818-9.818 9.818z"/>
                          </svg>
                          {new Date(entry.sent_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                          {' '}({entry.action === 'created' ? 'Create' : entry.action === 'resend' ? 'Resend' : 'Update'})
                        </div>
                      ))}
                    </div>
                  )}
                  
                  <div className="mt-4 pt-4 border-t space-y-2">
                    <div className="flex gap-2">
                      <button onClick={() => startEditEvent(event)} className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 border-2 border-blue-200 text-blue-600 rounded-lg hover:bg-blue-50 transition text-sm font-medium">
                        <Clock className="w-4 h-4" />
                        Edit Event
                      </button>
                      <button onClick={() => confirmDeleteEvent(event.id)} className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 border-2 border-red-200 text-red-600 rounded-lg hover:bg-red-50 transition text-sm font-medium">
                        <Trash2 className="w-4 h-4" />
                        Delete Event
                      </button>
                    </div>
                    {/* (2 Oct 2026) Was a hover-only tooltip behind an ⓘ, which a phone
                        never shows (a tap shares straight away), and its list no longer
                        matched the message. Now one visible line under the button. */}
                    <div>
                      <button onClick={() => shareToWhatsApp(event)} className="w-full flex items-center justify-center gap-2 bg-green-600 text-white py-4 rounded-xl hover:bg-green-700 transition text-sm font-semibold">
                        <Share2 className="w-4 h-4" />
                        Share this event via WhatsApp
                      </button>
                      <p className="text-xs text-gray-500 text-center mt-1">
                        Opens WhatsApp with the event's details and a link to Calendar Hub, ready to post to the group.
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* WhatsApp Notification Prompt */}
        {/* Share Calendar Section */}
        {/* (7 Oct 2026, 8.13) Space between the last event card and this panel: the same as between cards (mb-4 = space-y-4). */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <Share2 className="w-5 h-5 text-blue-600" />
            <h3 className="text-lg font-semibold text-gray-800">Subscribe and share</h3>
          </div>
          {/* (3 Oct 2026, F1) Each person has their own link now. */}
          <p className="text-sm text-gray-600 mb-2">
            The first two buttons add this calendar to <strong>your own</strong> phone or computer. Each member has their own link, found in Calendar Hub once they sign in.
          </p>
          <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-4">
            Your link is personal to you. Sharing it with non-calendar members could expose personal data such as calendar appointments and meeting locations - please do not distribute.
          </p>

          <div className="space-y-3">
            {/* iPhone/Mac Calendar — (30 Sep 2026, H21) now first, as on the member screen */}
            <div className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Apple className="w-4 h-4 text-gray-500" />
                <span className="text-sm font-medium text-gray-700">For iPhone/Mac Users</span>
              </div>
              <p className="text-xs text-gray-500 mb-2">One-tap subscribe (fastest!)</p>
              <a
                href={myFeedLink ? myFeedLink.webcalUrl : undefined}
                className="w-full bg-gradient-to-r from-blue-500 to-indigo-600 text-white px-4 py-2 rounded-lg hover:from-blue-600 hover:to-indigo-700 transition text-sm font-medium flex items-center justify-center gap-2"
              >
                <Apple className="w-4 h-4" />
                Subscribe (iPhone/Mac)
              </a>
            </div>

            {/* Google Calendar URL */}
            <div className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Link2 className="w-4 h-4 text-gray-500" />
                <span className="text-sm font-medium text-gray-700">For Google Calendar Users</span>
              </div>
              <p className="text-xs text-gray-500 mb-2">Desktop or mobile browser</p>
              <button
                onClick={() => {
                  if (!myFeedLink) { showStatus('Your calendar link is still loading. Please try again.', 'error'); return; }
                  navigator.clipboard.writeText(myFeedLink.feedUrl);
                  showStatus('URL copied to clipboard', 'success');
                }}
                className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition w-full justify-center"
              >
                <Link2 className="w-4 h-4" />
                Copy Subscription URL
              </button>
            </div>

            {/* Share to WhatsApp */}
            <div className="border border-blue-200 bg-blue-50 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Share2 className="w-4 h-4 text-blue-600" />
                <span className="text-sm font-medium text-blue-700">Quick Share</span>
              </div>
              <button
                onClick={shareHowToSubscribe}
                className="w-full bg-green-600 text-white px-4 py-4 rounded-lg hover:bg-green-700 transition text-sm font-medium flex items-center justify-center gap-2"
              >
                <Share2 className="w-4 h-4" />
                Tell the group how to subscribe, via WhatsApp
              </button>
              <p className="text-xs text-gray-500 text-center mt-1">
                Sends the steps, not a link to the calendar itself: each member uses their own link.
              </p>
            </div>
          </div>

          {/* Helper text */}
          <div className="mt-4 p-3 bg-gray-50 rounded-lg">
            <p className="text-xs text-gray-600">
              💡 <strong>Tip:</strong> iPhone users get the fastest experience with Apple Calendar (5-15 min sync). 
              Google Calendar takes up to 24 hours to sync.
            </p>
          </div>
        </div>
        </div>
        {/* (decision 77) End of the part hidden while History is showing; the pop-up
            windows below stay available over it. */}

        {/* WhatsApp Notification Prompt */}
        {whatsappNotifyModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-xl max-w-sm w-full p-6">
              {/* (7 Oct 2026, decision 19) One button per way of telling members, then Done. */}
              {notifySequence(whatsappNotifyModal) && (
                <p className="text-xs font-semibold text-gray-500 mb-1">
                  {notifySequence(whatsappNotifyModal)}
                </p>
              )}
              <p className="text-sm font-semibold text-gray-800 mb-3">
                {notifyHeading(whatsappNotifyModal)}
              </p>
              <div className="space-y-2">
                {eventEmail.count > 0 && (
                  eventEmail.status === 'sent' ? (
                    <p className="w-full py-3 rounded-lg text-sm font-semibold text-center text-blue-700 bg-blue-50 border border-blue-200">
                      {`Emailed ${eventEmail.sent} member${eventEmail.sent === 1 ? '' : 's'} \u2713`}
                    </p>
                  ) : (
                    <button
                      onClick={() => sendEventEmails(whatsappNotifyModal)}
                      disabled={eventEmail.status === 'sending'}
                      className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 transition text-sm disabled:opacity-60"
                    >
                      {eventEmail.status === 'sending'
                        ? 'Sending emails\u2026'
                        : `Email the ${eventEmail.count} member${eventEmail.count === 1 ? '' : 's'} who receive${eventEmail.count === 1 ? 's' : ''} event emails`}
                    </button>
                  )
                )}
                {waOpened ? (
                  <p className="w-full py-3 rounded-lg text-sm font-semibold text-center text-green-700 bg-green-50 border border-green-200">
                    {'WhatsApp opened \u2713'}
                  </p>
                ) : (
                  <button
                    onClick={() => { setWaOpened(true); handleSendWaNotification(whatsappNotifyModal, { keepOpen: true }); }}
                    className="w-full bg-green-600 text-white py-3 rounded-lg font-semibold hover:bg-green-700 transition text-sm flex items-center justify-center gap-2"
                  >
                    <MessageSquare className="w-4 h-4" />
                    Send via WhatsApp
                  </button>
                )}
                <button
                  onClick={() => setWhatsappNotifyModal(null)}
                  disabled={eventEmail.status === 'sending'}
                  className="w-full border border-gray-300 text-gray-600 py-3 rounded-lg font-semibold hover:bg-gray-50 transition text-sm flex items-center justify-center disabled:opacity-60"
                >
                  Done
                </button>
              </div>
              {/* (27 Sep 2026, A6) Closing without WhatsApp leaves the bell notice in place. */}
              {whatsappNotifyModal.gaNotificationId && !waOpened && (
                <p className="text-xs text-gray-500 mt-3 text-center">
                  If you close this without sending, it stays under Notifications until you send or dismiss it there.
                </p>
              )}
            </div>
          </div>
        )}

        {/* Tier Error Modal */}
        {tierError && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-xl max-w-sm w-full p-6">

              {/* Icon + title */}
              {tierError.code === 'TIER_EXPIRED' && (
                <div className="text-center mb-4">
                  <div className="text-4xl mb-2">⏰</div>
                  <h3 className="text-lg font-bold text-gray-900">Subscription Expired</h3>
                </div>
              )}
              {tierError.code === 'EVENT_LIMIT' && (
                <div className="text-center mb-4">
                  <div className="text-4xl mb-2">📋</div>
                  <h3 className="text-lg font-bold text-gray-900">Event Limit Reached</h3>
                </div>
              )}
              {tierError.code === 'OUTSIDE_PERIOD' && (
                <div className="text-center mb-4">
                  <div className="text-4xl mb-2">📅</div>
                  <h3 className="text-lg font-bold text-gray-900">Date Too Far Ahead</h3>
                </div>
              )}
              {tierError.code === 'SPACING_VIOLATION' && (
                <div className="text-center mb-4">
                  <div className="text-4xl mb-2">⏱️</div>
                  <h3 className="text-lg font-bold text-gray-900">Events Too Close Together</h3>
                </div>
              )}

              {/* Server message — reformat any M/D/YYYY dates to user's preferred format */}
              <p className="text-sm text-gray-600 mb-5 text-center">
                {tierError.message.replace(/\d{1,2}\/\d{1,2}\/\d{4}/g, (d) => {
                  const [m, day, y] = d.split('/').map(Number);
                  const dt = new Date(y, m - 1, day);
                  const dd = dt.getDate(), mm = dt.getMonth() + 1, yyyy = dt.getFullYear();
                  const names = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
                  const fmt = userTier?.preferred_date_format || 'DD-MMM-YYYY';
                  if (fmt === 'MM/DD/YYYY') return `${mm}/${dd}/${yyyy}`;
                  if (fmt === 'DD/MM/YYYY') return `${dd}/${mm}/${yyyy}`;
                  if (fmt === 'YYYY-MM-DD') return `${yyyy}-${String(mm).padStart(2,'0')}-${String(dd).padStart(2,'0')}`;
                  return `${dd}-${names[mm-1]}-${yyyy}`;
                })}
              </p>

              {/* Actions */}
              <div className="space-y-2">

                {/* (19 Sep 2026) The tier-era "View Subscription Options" and "Delete Calendar &amp; Start Fresh"
                    buttons were removed here: limits are now per-calendar and set by the System Admin (no
                    plans exist), and OUTSIDE_PERIOD now means "too far ahead", where deleting the calendar
                    would be exactly the wrong advice. */}

                {/* Edit Event — dismiss modal, leave form open to adjust */}
                <button
                  onClick={() => setTierError(null)}
                  className="w-full border border-gray-300 text-gray-600 py-2.5 rounded-lg font-medium hover:bg-gray-50 transition text-sm"
                >
                  Edit Event
                </button>

              </div>
            </div>
          </div>
        )}

        {/* Delete Calendar Confirmation Dialog */}
        {showRenameCalendar && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md mx-4 w-full">
              <h3 className="text-lg font-bold text-gray-900 mb-2">Rename Calendar</h3>
              <p className="text-gray-600 mb-4">Enter a new name for "{currentCalendar?.name}".</p>
              <input
                type="text"
                value={renameCalendarInput}
                onChange={(e) => setRenameCalendarInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleRenameCalendar(); if (e.key === 'Escape') setShowRenameCalendar(false); }}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none mb-4"
                autoFocus
              />
              {/* Calendar timezone picker */}
              <div className="mb-4">
                <label className="block text-sm font-semibold text-gray-700 mb-1">Calendar timezone</label>
                <p className="text-xs text-gray-500 mb-2">All events default to this timezone unless overridden per-event.</p>
                <select
                  value={calendarTimezone}
                  onChange={(e) => setCalendarTimezone(e.target.value)}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none text-sm"
                >
                  {TIMEZONES.map(tz => (
                    <option key={tz.value} value={tz.value}>{tz.label}</option>
                  ))}
                </select>
              </div>

              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => setShowRenameCalendar(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    // Save name if changed
                    if (renameCalendarInput.trim() && renameCalendarInput.trim() !== currentCalendar?.name) {
                      await handleRenameCalendar();
                    }
                    // Save timezone if changed
                    if (calendarTimezone !== (currentCalendar?.timezone || 'Europe/London')) {
                      await handleSaveCalendarTimezone(calendarTimezone);
                    }
                    setShowRenameCalendar(false);
                  }}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Save changes
                </button>
              </div>
            </div>
          </div>
        )}

        {showDeleteConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md mx-4">
              <h3 className="text-lg font-bold text-gray-900 mb-2">Delete Calendar?</h3>
              <p className="text-gray-600 mb-4">
                This will permanently delete "{currentCalendar?.name}" and all {events.length} event{events.length !== 1 ? 's' : ''}. 
                This action cannot be undone.
              </p>
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteCalendar}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                >
                  Delete Calendar
                </button>
              </div>
            </div>
          </div>
)}

        {/* DELETE EVENT CONFIRMATION MODAL */}
        {showDeleteEventConfirm && eventToDelete && (() => {
          const delEvent = events.find(e => e.id === eventToDelete);
          // (5 Oct 2026, H18 Stage 1) An event on a hosting-schedule month: say what
          // happens to the month, so members are not left reading "Day to be confirmed"
          // for a meeting that is off.
          // (6 Oct 2026, H18 Stage 2) Not asked for a one-off: its row simply goes with it.
          const onSchedule = currentCalendar?.host_board_enabled === true
            && boardEventInfo && boardEventInfo.uids.includes(eventToDelete);
          const delMonth = (boardEventInfo && boardEventInfo.labels[eventToDelete])
            || (delEvent && delEvent.date
              ? new Date(String(delEvent.date).slice(0, 10) + 'T00:00:00Z').toLocaleDateString('en-GB', { month: 'long', year: 'numeric', timeZone: 'UTC' })
              : 'this month');
          const choiceClass = (value) => `flex items-start gap-2 p-2.5 rounded-lg border cursor-pointer ${deleteMonthChoice === value ? 'border-blue-400 bg-blue-50' : 'border-gray-200'}`;
          // (decision 62) Only a past meeting that is in the history, on a calendar keeping one.
          const delReading = delEvent ? historyReadingOf(currentCalendar, delEvent.description) : { inHistory: false, keywordValue: null };
          const delDateIso = delEvent && delEvent.date ? String(delEvent.date).slice(0, 10) : '';
          const askHistory = delReading.inHistory && !!delDateIso && delDateIso < new Date().toISOString().slice(0, 10);
          const delWhat = delReading.keywordValue || `"${delEvent?.title || 'This meeting'}"`;
          const historyChoiceClass = (value) => `flex items-start gap-2 p-2.5 rounded-lg border cursor-pointer ${deleteHistoryChoice === value ? 'border-blue-400 bg-blue-50' : 'border-gray-200'}`;
          return (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md mx-4">
              <h3 className="text-lg font-bold text-gray-900 mb-2">Delete Event?</h3>
              <p className="text-gray-600 mb-4">
                This will permanently delete "{delEvent?.title}". 
                This action cannot be undone.
              </p>
              {onSchedule && (
                <div className="mb-4 space-y-2">
                  <p className="text-sm font-semibold text-gray-800">What should the hosting schedule show for {delMonth}?</p>
                  <label className={choiceClass('off')}>
                    <input
                      type="radio"
                      name="delete-month-choice"
                      className="mt-1"
                      checked={deleteMonthChoice === 'off'}
                      onChange={() => { setDeleteMonthChoice('off'); setDeleteMonthAsk(false); }}
                    />
                    <span className="text-sm text-gray-700">
                      <span className="font-medium">The meeting is off.</span> {delMonth} shows "No meeting"; its host and any offers are cleared.
                    </span>
                  </label>
                  {deleteMonthChoice === 'off' && (
                    <label className="block text-xs font-semibold text-gray-600 pl-8">
                      Note members see on the schedule (optional)
                      <input
                        type="text"
                        value={deleteMonthNote}
                        maxLength={500}
                        onChange={(e) => setDeleteMonthNote(e.target.value)}
                        className="mt-1 block w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg font-normal"
                      />
                    </label>
                  )}
                  <label className={choiceClass('redate')}>
                    <input
                      type="radio"
                      name="delete-month-choice"
                      className="mt-1"
                      checked={deleteMonthChoice === 'redate'}
                      onChange={() => { setDeleteMonthChoice('redate'); setDeleteMonthAsk(false); }}
                    />
                    <span className="text-sm text-gray-700">
                      <span className="font-medium">It will be re-dated.</span> {delMonth} shows "Day to be confirmed"; the host stays, to reconfirm once there is a new day.
                    </span>
                  </label>
                  {deleteMonthAsk && (
                    <p className="text-sm text-red-600">Please choose one of these first.</p>
                  )}
                </div>
              )}
              {askHistory && (
                <div className="mb-4 space-y-2">
                  <p className="text-sm font-semibold text-gray-800">
                    {delWhat} ({formatIsoDateToPref(delDateIso, userTier?.preferred_date_format)}) is in the calendar history. Keep it there?
                  </p>
                  <label className={historyChoiceClass('keep')}>
                    <input
                      type="radio"
                      name="delete-history-choice"
                      className="mt-1"
                      checked={deleteHistoryChoice === 'keep'}
                      onChange={() => setDeleteHistoryChoice('keep')}
                    />
                    <span className="text-sm text-gray-700">
                      <span className="font-medium">Keep it in the history.</span> The meeting stays on the History page as it is now{currentCalendar?.history_show_menu === true ? ', with its dishes' : ''}.
                    </span>
                  </label>
                  <label className={historyChoiceClass('delete')}>
                    <input
                      type="radio"
                      name="delete-history-choice"
                      className="mt-1"
                      checked={deleteHistoryChoice === 'delete'}
                      onChange={() => setDeleteHistoryChoice('delete')}
                    />
                    <span className="text-sm text-gray-700">
                      <span className="font-medium">Delete it from the history too.</span> It disappears from the History page.
                    </span>
                  </label>
                </div>
              )}
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => {
                    setShowDeleteEventConfirm(false);
                    setEventToDelete(null);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    if (onSchedule && !deleteMonthChoice) { setDeleteMonthAsk(true); return; }
                    deleteEvent({
                      ...(onSchedule ? { monthOutcome: deleteMonthChoice, monthNote: deleteMonthNote } : {}),
                      ...(askHistory ? { historyChoice: deleteHistoryChoice } : {}),
                    });
                  }}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                >
                  Delete Event
                </button>
              </div>
            </div>
          </div>
          );
        })()}

        {/* (11 Oct 2026, 8.37) RUN SCHEDULER — what it does, then Run now / Cancel. */}
        {confirmRunScheduler && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg p-6 max-w-md w-full">
              <h3 className="text-lg font-bold text-gray-900 mb-2">Run the scheduler now?</h3>
              <p className="text-gray-700 mb-2">
                The scheduler runs by itself every day at 07:00, French time. Running it now does the same
                straight away, for every calendar in Calendar Hub: it opens, reminds and closes any polls that are
                due, and checks the hosting schedule, if present.
              </p>
              <p className="text-sm text-gray-500 mb-4">Members are emailed as on a normal run.</p>
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setConfirmRunScheduler(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => { setConfirmRunScheduler(false); handleRunScheduler(); }}
                  className="px-4 py-2 rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700"
                >
                  Run now
                </button>
              </div>
            </div>
          </div>
        )}

        {/* (11 Oct 2026, Stage 3, decision 92) EDIT EVENT: a past meeting's date changed and
            its history record is on the old date. Asked each time. */}
        {editQuestion && editQuestion.type === 'history' && (() => {
          const full = (iso) => new Date(String(iso).slice(0, 10) + 'T00:00:00Z')
            .toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' });
          const what = editQuestion.name ? `${editQuestion.name} (${full(editQuestion.oldDate)})` : `The meeting of ${full(editQuestion.oldDate)}`;
          return (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-lg p-6 max-w-md w-full">
                <h3 className="text-lg font-bold text-gray-900 mb-2">Calendar history</h3>
                <p className="text-gray-700 mb-4">
                  {what} is recorded in the calendar history. Move the history record to <span className="whitespace-nowrap">{full(editQuestion.newDate)}</span> too?
                </p>
                <div className="space-y-3 mb-4">
                  <div>
                    <button
                      type="button"
                      onClick={() => onEditQuestionAnswer({ moveHistory: true })}
                      className="w-full px-4 py-2.5 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700"
                    >
                      Yes, move it
                    </button>
                    <p className="text-xs text-gray-600 mt-1 px-1">The History page keeps one row for this meeting, on the new date.</p>
                  </div>
                  <div>
                    <button
                      type="button"
                      onClick={() => onEditQuestionAnswer({ moveHistory: false })}
                      className="w-full px-4 py-2.5 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700"
                    >
                      No, leave it
                    </button>
                    <p className="text-xs text-gray-600 mt-1 px-1">The record stays on <span className="whitespace-nowrap">{full(editQuestion.oldDate)}</span>; the event shows on the new date as well.</p>
                  </div>
                </div>
                <div className="flex justify-end">
                  <button type="button" onClick={onCancelEditQuestion} className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">
                    Back to the form
                  </button>
                </div>
              </div>
            </div>
          );
        })()}

        {/* (11 Oct 2026, Stage 3) EDIT EVENT: the poll dates for the new date. */}
        {editQuestion && editQuestion.type === 'poll' && (
          <PollDatesDialog
            key={JSON.stringify(editQuestion.timetable)}
            timetable={editQuestion.timetable}
            eventTitle={newEvent && newEvent.title}
            onAnswer={onEditQuestionAnswer}
            onCancel={onCancelEditQuestion}
          />
        )}

        {/* (24 Oct 2026, decision 131) POLL DATES for an event that is not moving. */}
        {pollDatesQuestion && (
          <PollDatesDialog
            key={`${pollDatesQuestion.eventUid}-${JSON.stringify(pollDatesQuestion.timetable)}`}
            timetable={pollDatesQuestion.timetable}
            eventTitle={pollDatesQuestion.title}
            mode={pollDatesQuestion.mode}
            onAnswer={onPollDatesAnswer}
            onCancel={onCancelPollDates}
          />
        )}

        {/* (6 Oct 2026, H18 Stage 2) HOSTING SCHEDULE QUESTION — raised by Add Event
            (or "Add it to the hosting schedule only for now") when the event's month
            needs a decision. The form stays open behind it; Cancel changes nothing. */}
        {scheduleChoice && (() => {
          const c = scheduleChoice;
          const month = c.monthLabel || 'this month';
          // (7 Oct 2026, decisions 6 and 13) A month can already hold two or more meetings:
          // name them all, offer "Instead of {day}" for each (Add Event only), and call the
          // new one "a third ... meeting". One meeting: the wording is as before.
          // (7 Oct 2026, decision 20) Listed in date order ("Fri 27 Nov and Sat 28 Nov"),
          // not by row number; a meeting with no date yet goes last.
          const meetingDate = (m) => m.date || (m.existingEvent && m.existingEvent.date) || '9999-12-31';
          const meetings = (Array.isArray(c.meetings) && c.meetings.length > 0
            ? [...c.meetings]
            : [{ slotId: c.slotId, existingEvent: c.existingEvent, existingDay: c.existingDay }])
            .sort((a, b) => meetingDate(a).localeCompare(meetingDate(b)));
          const n = meetings.length;
          const NUMBER_WORDS = ['', 'one', 'two', 'three', 'four', 'five'];
          const ORDINALS = ['', 'first', 'second', 'third', 'fourth', 'fifth'];
          const nextOrdinal = ORDINALS[n + 1] || 'another';
          const daysList = (() => {
            const days = meetings.map((m) => m.existingDay || 'day to be confirmed');
            return days.length <= 1 ? days.join('') : `${days.slice(0, -1).join(', ')} and ${days[days.length - 1]}`;
          })();
          const alreadyLine = n === 1
            ? `There's already a meeting in ${month} (${daysList}).`
            : `There are already ${NUMBER_WORDS[n] || n} meetings in ${month} (${daysList}).`;
          // (7 Oct 2026) A cancelled ("No meeting") row keeps its number, so the new row can be
          // #3 although it is only the second meeting: then name the row it will actually be.
          const nextLabelNote = c.nextSeq && c.nextSeq !== n + 1
            ? `It shows as "${month} #${c.nextSeq}".`
            : n === 1
            ? `The two show as "${month} #1" and "#2".`
            : (n + 1 <= 5
              ? `The ${NUMBER_WORDS[n + 1]} show as ${Array.from({ length: n + 1 }, (_, i) => (i === 0 ? `"${month} #1"` : `"#${i + 1}"`)).slice(0, -1).join(', ')} and "#${n + 1}".`
              : `It shows as "${month} #${c.nextSeq || n + 1}".`);
          let question = '';
          let answers = [];
          if (c.source === 'scheduleOnly') {
            if (c.situation === 'has_row') {
              question = `${alreadyLine} Add a ${nextOrdinal} ${month} meeting?`;
              answers = [
                { mode: 'second', label: `Add a ${nextOrdinal} ${month} meeting`, note: nextLabelNote },
              ];
            } else if (c.situation === 'no_meeting') {
              question = `${month} is marked as no meeting. Is it on after all?`;
              answers = [{ mode: 'reopen', label: "Yes, it's on", note: `${month} shows this day, proposed.` }];
            }
          } else if (c.situation === 'has_event') {
            const insteadNote = (ev) => (ev
              ? `"${ev.title}" will be deleted and this event takes its place. You'll be offered the usual messages for both.`
              : 'The existing event will be deleted and this event takes its place.');
            // (7 Oct 2026, decision 18) The question names the new event, and each answer says
            // what happens to it: "Replaces the Fri 20 Nov event", "Becomes a third ... event",
            // "Is a one-off".
            const newName = newEvent && newEvent.title && newEvent.title.trim()
              ? `"${newEvent.title.trim()}"`
              : 'this event';
            question = n === 1
              ? `${alreadyLine} Does ${newName} replace it, become a ${nextOrdinal} ${month} event, or is it a one-off?`
              : `${alreadyLine} Does ${newName} replace one of them, become a ${nextOrdinal} ${month} event, or is it a one-off?`;
            answers = meetings.map((m) => ({
              mode: 'instead',
              key: `instead-${m.slotId}`,
              label: `Replaces the ${m.existingDay || 'existing'} event`,
              note: insteadNote(m.existingEvent),
              pick: { slotId: m.slotId, existingEvent: m.existingEvent },
            }));
            answers = [
              ...answers,
              { mode: 'second', label: `Becomes a ${nextOrdinal} ${month} event`, note: nextLabelNote },
              { mode: 'one_off', label: 'Is a one-off', note: "In the calendar as an event, as usual, but it will not appear in members' view of the hosting schedule." },
            ];
          } else if (c.situation === 'not_on_schedule') {
            question = `Add this event to the hosting schedule as ${month}, or is it a one-off?`;
            answers = [
              { mode: 'new_row', label: `Add it as ${month}`, note: 'Members see it on the hosting schedule as usual.' },
              { mode: 'one_off', label: 'A one-off', note: "In the calendar as an event, as usual, but it will not appear in members' view of the hosting schedule." },
            ];
          } else if (c.situation === 'no_meeting') {
            question = `${month} is marked as no meeting. Is it on after all?`;
            answers = [
              { mode: 'reopen', label: "Yes, it's on", note: `${month} shows this event again.` },
              { mode: 'one_off', label: "No, it's a one-off", note: `${month} stays "No meeting"; members don't see this event on the hosting schedule.` },
            ];
          }
          return (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg p-6 max-w-md mx-4">
                <h3 className="text-lg font-bold text-gray-900 mb-2">Hosting schedule</h3>
                <p className="text-gray-700 mb-4">{question || 'Please check the hosting schedule and try again.'}</p>
                {/* (7 Oct 2026, decision 22) Each choice is a solid button holding only its
                    label; the explanation sits underneath, outside it, so it can be read and
                    selected without pressing the button. */}
                <div className="space-y-3 mb-4">
                  {answers.map((a) => (
                    <div key={a.key || a.mode}>
                      <button
                        type="button"
                        onClick={() => onScheduleChoice(a.mode, a.pick)}
                        className="w-full px-4 py-2.5 rounded-lg bg-blue-600 text-white text-sm font-semibold shadow-sm hover:bg-blue-700 active:bg-blue-800 transition cursor-pointer"
                      >
                        {a.label}
                      </button>
                      {a.note && <p className="text-xs text-gray-600 mt-1 px-1">{a.note}</p>}
                    </div>
                  ))}
                </div>
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={onCancelScheduleChoice}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          );
        })()}

        {/* (26 Oct 2026, decision 145) Withdraw / Restore event administrator rights. */}
        {eaConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md mx-4">
              <h3 className="text-lg font-bold text-gray-900 mb-2">
                {eaConfirm.withdraw ? 'Withdraw event administrator rights?' : 'Restore event administrator rights?'}
              </h3>
              <p className="text-gray-600 mb-4">
                {eaConfirm.withdraw
                  ? `${eaConfirm.event.eventAdmin?.name || 'The host'} will no longer be able to run the poll for "${eaConfirm.event.title}", straight away. They stay the host, and no message is sent to them.`
                  : `${eaConfirm.event.eventAdmin?.name || 'The host'} will again be able to run the poll for "${eaConfirm.event.title}". No message is sent to them.`}
              </p>
              <div className="flex flex-wrap gap-3 justify-end">
                <button
                  onClick={() => setEaConfirm(null)}
                  disabled={eaSaving}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={executeEventAdminChange}
                  disabled={eaSaving}
                  className={`px-4 py-2 rounded-lg text-white disabled:opacity-50 ${eaConfirm.withdraw ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'}`}
                >
                  {eaSaving ? 'Working…' : (eaConfirm.withdraw ? 'Withdraw' : 'Restore')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* (10 Sep 2026) Manual poll-control confirmation — same styled-modal
            pattern as Delete Event above, per the app's no-native-dialogs rule. */}
        {pollActionConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md mx-4">
              <h3 className="text-lg font-bold text-gray-900 mb-2">
                {pollActionConfirm.type === 'open'
                  ? 'Open Phase 1 poll now?'
                  : (pollActionConfirm.poll.phase === 'offer' ? 'Close Poll Phase 1 now?' : 'Close Poll Phase 2 now?')}
              </h3>
              <p className="text-gray-600 mb-4">
                {pollActionConfirm.type === 'open'
                  ? `This opens the offer-phase poll for "${pollActionConfirm.event.title}" immediately, regardless of the calendar's usual timing settings.`
                  : (pollActionConfirm.poll.phase === 'offer'
                      ? `This closes phase 1 (the offer-phase) of the poll for the "${pollActionConfirm.event.title}" event immediately and advances to Phase 2 (or flags zero offers, if none were made). This cannot be undone.`
                      : `This closes phase 2 (the confirm-phase) of the poll for the "${pollActionConfirm.event.title}" event immediately. This cannot be undone.`)}
              </p>
              {/* (26 Sep 2026, H3) Standing rule: any member email triggered by a GA
                  action goes through this prompt, with the count and a way not to send. */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4 text-sm text-gray-700">
                {pollEmailCount === undefined
                  ? 'Working out how many members would be emailed…'
                  : pollEmailCount === null
                    ? 'Members would be emailed according to their own email settings (the number could not be worked out).'
                    : pollEmailCount === 0
                      ? 'No members would be emailed (their email settings exclude this email).'
                      : `${pollEmailCount} member${pollEmailCount === 1 ? '' : 's'} would be emailed, according to their own email settings.`}
                {pollActionConfirm.type === 'close' && pollActionConfirm.poll.phase === 'offer' && (
                  <span className="block mt-1 text-gray-500">If no dishes were offered, Phase 2 won't open and no one is emailed.</span>
                )}
              </div>
              {(() => {
                const busy = pollActionInFlight === pollActionConfirm.event.id;
                const verb = pollActionConfirm.type === 'open' ? 'Open' : 'Close';
                const colour = pollActionConfirm.type === 'open' ? 'bg-blue-600 hover:bg-blue-700' : 'bg-red-600 hover:bg-red-700';
                const emailLabel = pollEmailCount === undefined
                  ? `${verb} and email members`
                  : pollEmailCount === null
                    ? `${verb} and email members`
                    : `${verb} and email ${pollEmailCount} member${pollEmailCount === 1 ? '' : 's'}`;
                return (
                  <div className="flex flex-wrap gap-3 justify-end">
                    <button
                      onClick={() => setPollActionConfirm(null)}
                      disabled={busy}
                      className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => executePollAction(false)}
                      disabled={busy}
                      className="px-4 py-2 border border-gray-300 rounded-lg text-gray-800 hover:bg-gray-50 disabled:opacity-50"
                    >
                      {busy ? 'Working…' : `${verb}, don't email`}
                    </button>
                    {pollEmailCount !== 0 && (
                      <button
                        onClick={() => executePollAction(true)}
                        disabled={busy || pollEmailCount === undefined}
                        className={`px-4 py-2 rounded-lg text-white disabled:opacity-50 ${colour}`}
                      >
                        {busy ? 'Working…' : emailLabel}
                      </button>
                    )}
                  </div>
                );
              })()}
            </div>
          </div>
        )}

        {showSettings && (
          <SettingsModal 
            isOpen={showSettings} 
            onClose={() => { setShowSettings(false); setSettingsInitialTab('profile'); }}
            userEmail={userEmail}
            apiBaseUrl={API_BASE_URL}
            authToken={localStorage.getItem('auth_token')}
            onStatusMessage={showStatus}
            userTier={userTier}
            initialTab={settingsInitialTab}
            currentCalendar={currentCalendar}
            firstName={firstName}
            lastName={lastName}
            isSA={isSA}
            raRegions={raRegions}
            authorityCalendars={authorityCalendars}
            onSaveProfile={(fn, ln, pn) => { setFirstName(fn); setLastName(ln); setPreferredName(pn); }}
            restrictToProfileTabs={step === 'member-calendar'}
            restrictToDeputyTabs={!!currentCalendar?.is_deputy}
            onAnswerPollForMember={onAnswerPollForMember}
            onCalendarOptionsSaved={handleCalendarOptionsSaved}
          />
        )}

        {/* USER GUIDE MODAL */}
        <UserGuideModal
          isOpen={showUserGuide}
          onClose={() => setShowUserGuide(false)}
          isOnDashboard={step === 'main'}
          userPreferredCurrency={userPreferredCurrency}
          calendarGuide={currentCalendar?.guide_url ? { name: currentCalendar.name, url: currentCalendar.guide_url } : null}
        />
      </div>
    </div>
  );
};

export default DashboardStep;
